import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CircularProgress, Modal, Tooltip, Paper, Tab, Tabs, Box, ToggleButton, ToggleButtonGroup, Switch, FormControlLabel, Checkbox, TextField, Button, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import { Close, Edit, Check, History, SwapHoriz, FilterList, ArrowUpward, ArrowDownward, ArrowForward, Save, Pause, PlayArrow, Delete } from '@mui/icons-material';
import Select from 'react-select';
import { twMerge } from 'tailwind-merge';
import { useDockData, DockDoor, Status, SpotGroup, DockType, HistoryEntry, DockHistory, performDockAction, YardSpot } from './dockDataFetch';
import { formatDirection } from '../../utils/shipmentHelpers';
import { getAvailableCTAs, CTAConfig } from '../../utils/ctaLogic';
import { isDockUnavailableForMovement } from '../../utils/dock';
import { createSafetyCheck } from '../../api/safetyCheck';
import { useAdvanceTrailerStatus } from '../../hooks/useAdvanceTrailerStatus';
import axios from 'axios';
import TaskDetailsModal from '../../components/TaskDetailsModal/TaskDetailsModal';
import { getAuthToken } from '../../utils/cookieUtils';
import { 
  saveFacilityToStorage, 
  loadFacilityFromStorage, 
  formatFacilityOptions, 
  findFacilityById,
  type Facility
} from '../../utils/facilityStorage';

// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Helper to merge lock sets
const mergeLockSets = (lockSet1: Set<string>, lockSet2: Set<string>): Set<string> => {
  const merged = new Set<string>();
  lockSet1.forEach(id => merged.add(id));
  lockSet2.forEach(id => merged.add(id));
  return merged;
};

// Helper function to format dates in military time without timezone conversion
const formatMilitaryDateTime = (dateString: string | Date): string => {
  const dateStr = typeof dateString === 'string' ? dateString : dateString.toISOString();
  
  // Parse the date string directly without timezone conversion
  // Remove timezone offset (e.g., +00:00, -05:00, +0000, -0500, Z) from the end
  const cleanDateStr = dateStr.replace(/([+-]\d{2}:?\d{2}|Z)$/, '');
  
  // Split into date and time parts - handle both 'T' and space separators
  const [datePart, timePart] = cleanDateStr.includes('T') 
    ? cleanDateStr.split('T') 
    : cleanDateStr.split(' ');
  
  if (!datePart || !timePart) return dateStr;
  
  // Format date as MM/DD/YYYY
  const [year, month, day] = datePart.split('-');
  const formattedDate = `${month}/${day}/${year}`;
  
  // Extract hours and minutes for military time (HH:MM)
  // Handle microseconds by splitting on '.' first
  const timeOnly = timePart.split('.')[0];
  const [hours, minutes] = timeOnly.split(':');
  const militaryTime = `${hours}:${minutes}`;
  
  return `${formattedDate} ${militaryTime}`;
};

type DoorAction = {
  label: string | ((door: DockDoor) => string);
  color: string;
  icon: React.ReactNode;
  apiEndpoint: string;
  showWhen: (door: DockDoor) => boolean;
};

const doorActions: DoorAction[] = [
  {
    label: 'Send to Yard',
    color: 'bg-blue-500 hover:bg-blue-600',
    icon: <SwapHoriz fontSize="small" className="mr-1" />,
    apiEndpoint: '/api/v1/admin/drivers/send_to_yard',
    showWhen: (door) => door.current_status === 'ready_for_yard'
  },
  {
    label: 'Safety Check Complete',
    color: 'bg-green-500 hover:bg-green-600',
    icon: <Check fontSize="small" className="mr-1" />,
    apiEndpoint: `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/update_loading_status`,
    showWhen: (door) => door.current_status === 'needs safety check'
  },
  {
    label: (door) => door.direction === 'inbound' ? 'Start Unloading' : 'Start Loading',
    color: 'bg-blue-500 hover:bg-blue-600',
    icon: <ArrowForward fontSize="small" className="mr-1" />,
    apiEndpoint: `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/update_loading_status`,
    showWhen: (door) => door.current_status === 'safety check complete'
  },
  {
    label: 'Loading Complete',
    color: 'bg-green-500 hover:bg-green-600',
    icon: <Check fontSize="small" className="mr-1" />,
    apiEndpoint: `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/update_loading_status`,
    showWhen: (door) => door.current_status === 'loading started'
  },
  {
    label: 'Unloading Complete',
    color: 'bg-green-500 hover:bg-green-600',
    icon: <Check fontSize="small" className="mr-1" />,
    apiEndpoint: `${process.env.REACT_APP_WRITE_API_URL}/api/v1/admin/drivers/update_loading_status`,
    showWhen: (door) => door.current_status === 'unloading started'
  }
  // Add more actions as needed
];

const DockManagement = () => {
  // Get real dock data from API using our custom hook
  const {
    loading: apiLoading,
    dockDoors: apiDockDoors,
    dockHistory: apiDockHistory,
    error,
    facilities,
    selectedFacility,
    setSelectedFacility,
    timeInterval,
    setTimeInterval,
    colorConfig,
    setColorConfig,
    // New GraphQL-related functionality
    pendingDockChanges,
    setPendingDockChanges,
    pendingFacilityChanges,
    setPendingFacilityChanges,
    savingChanges,
    getPendingChangesCount,
    saveAllChanges,
    performDockAction: performGraphQLDockAction,
    // Yard data from combined hook
    yardSpots,
    zones,
    facilityZones,
    // Task-based locks
    taskBasedLockedDoors,
    taskBasedLockedSpots,
    activeLocks,
    getLocksForDockDoor,
    // Modal state for deferred updates
    isModalOpen,
    setIsModalOpen,
    hasStagedData,
    hasStagedDataForResource,
    // Active resource tracking for smart merging
    setActiveResourceIds
  } = useDockData();

  // Hook for advancing trailer status
  const { handleAdvanceStatus, loading: advanceStatusLoading } = useAdvanceTrailerStatus();

  // Router location for deep linking
  const location = useLocation();

  // Set time interval to max always
  useEffect(() => {
    setTimeInterval(8760); // Always set to max (1 year)
  }, [setTimeInterval]);

  // Local state
  const [selectedDoor, setSelectedDoor] = useState<DockDoor | null>(null);
  const [showDoorDetailsModal, setShowDoorDetailsModal] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [dockTypeFilter, setDockTypeFilter] = useState<DockType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [shipmentTypeFilter, setShipmentTypeFilter] = useState<'all' | 'Drop' | 'Live'>('all');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryDoor, setSelectedHistoryDoor] = useState<DockDoor | null>(null);
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('today');
  const [showYardMoveModal, setShowYardMoveModal] = useState(false);
  const [selectedYardSpot, setSelectedYardSpot] = useState<string>('');
  const [selectedYardSpotId, setSelectedYardSpotId] = useState<string>('');
  const [autoCompleteMoveToYard, setAutoCompleteMoveToYard] = useState(false);
  const [movingToYard, setMovingToYard] = useState(false);
  const [moveToYardError, setMoveToYardError] = useState<string | null>(null);
  
  // Removed old facility zone modal - now handled by unified Move to Yard modal
  
  // For manual/other locks (not task-based) - currently empty but keeping for consistency with YardSpotManagement
  const [lockedSpots] = useState<Set<string>>(new Set());
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdTask, setCreatedTask] = useState<any>(null);
  const [lockedDoors, setLockedDoors] = useState<Set<string>>(new Set());
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configModalTab, setConfigModalTab] = useState(0);
  
  // Add state for unified yard/zone selection modal (exact same as YardSpotManagement)
  const [showMoveToYardModal, setShowMoveToYardModal] = useState(false);
  const [selectedDestinationType, setSelectedDestinationType] = useState<string>('yard_spot');
  const [selectedYardSpotForMove, setSelectedYardSpotForMove] = useState<string>('');
  const [selectedYardSpotUuid, setSelectedYardSpotUuid] = useState<string>('');
  const [selectedZoneFilter, setSelectedZoneFilter] = useState<string | null>(null);
  const [spotFilter, setSpotFilter] = useState<string>('');
  const [selectedFacilityZone, setSelectedFacilityZone] = useState<string>('');
  const [movingToDestination, setMovingToDestination] = useState(false);
  const [moveToDestinationError, setMoveToDestinationError] = useState<string | null>(null);
  const [validatingShipmentForYard, setValidatingShipmentForYard] = useState(false);
  
  // Add Move to Dock modal state
  const [showMoveToDockModal, setShowMoveToDockModal] = useState(false);
  const [selectedTargetDockDoor, setSelectedTargetDockDoor] = useState<string>('');
  const [selectedTargetDockDoorId, setSelectedTargetDockDoorId] = useState<string>('');
  const [dockMoveFilter, setDockMoveFilter] = useState<string>('');
  const [movingToDock, setMovingToDock] = useState(false);
  const [moveToDockError, setMoveToDockError] = useState<string | null>(null);
  const [selectedShipmentreference_number, setSelectedShipmentreference_number] = useState<string>('');
  const [validatingShipment, setValidatingShipment] = useState(false);
  
  // Task details modal state
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState<any>(null);

  // Safety check state
  const [showSafetyCheckConfirm, setShowSafetyCheckConfirm] = useState(false);
  const [creatingSafetyCheck, setCreatingSafetyCheck] = useState(false);
  const [safetyCheckError, setSafetyCheckError] = useState<string | null>(null);
  
  // Sorting state
  const [sortCriteria, setSortCriteria] = useState<'order' | 'number' | 'status' | 'mdt' | 'detention' | 'dockTimer'>('order');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Deep linking state
  const [highlightedDoorUuid, setHighlightedDoorUuid] = useState<string | null>(null);

  // Handle deep linking to specific door
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const doorUuid = searchParams.get('door');
    
    if (doorUuid) {
      setHighlightedDoorUuid(doorUuid);
      
      // Scroll to the door after a short delay to ensure it's rendered
      setTimeout(() => {
        const doorElement = document.getElementById(`dock-door-${doorUuid}`);
        if (doorElement) {
          doorElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          
          // Auto-open the door details modal
          const targetDoor = apiDockDoors.find(door => door.id === doorUuid);
          if (targetDoor) {
            handleDoorClick(targetDoor);
          }
        }
        
        // Clear highlight after 5 seconds
        setTimeout(() => {
          setHighlightedDoorUuid(null);
        }, 5000);
      }, 500);
    }
  }, [location.search, apiDockDoors]);
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Open door details modal
  const handleDoorClick = (door: DockDoor) => {
    setShowDoorDetailsModal(true);
    setSelectedDoor(door);
    setTrailerNotesValue(door.trailerLineageNotes || '');
    setIsEditingTrailerNotes(false);
  };
  
  // Close door details modal
  const handleCloseDetailsModal = () => {
    setShowDoorDetailsModal(false);
    setSelectedDoor(null);
  };
  
  // Handle status change
  const handleStatusChange = (status: Status) => {
    if (!selectedDoor) return;
    
    // Track the change for later API update
    setPendingDockChanges(prev => ({
      ...prev,
      [selectedDoor.id]: {
        ...prev[selectedDoor.id],
        status: status
      }
    }));
    
    // Update local state immediately for UI feedback
    setSelectedDoor({ ...selectedDoor, status });
  };
  
  // Handle group change
  const handleGroupChange = (group: SpotGroup) => {
    if (!selectedDoor) return;
    
    // Track the change for later API update
    setPendingDockChanges(prev => ({
      ...prev,
      [selectedDoor.id]: {
        ...prev[selectedDoor.id],
        group: group
      }
    }));
    
    // Update local state immediately for UI feedback
    setSelectedDoor({ ...selectedDoor, group });
  };
  
  // Open history modal
  const handleShowHistory = (door: DockDoor) => {
    setSelectedHistoryDoor(door);
    setShowHistoryModal(true);
  };
  
  // Close history modal
  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedHistoryDoor(null);
  };

  // Handle pause/play toggle
  const handlePausePlayToggle = async (door: DockDoor) => {
    const newPausedState = !door.paused;

    try {
      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL API URL
      const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

      // Reconstruct the dock configuration with the new paused status
      // The configuration object should include status, notes, and paused fields
      const updatedConfig = {
        status: door.configurationStatus || 'available',
        notes: door.notes || null,
        paused: newPausedState
      };

      // Use the existing UPDATE_DOCK_MUTATION like in WarehouseManagement
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: `
            mutation UpdateDock($uuid: String!, $input: DockUpdateInput!) {
              update_dock(uuid: $uuid, input: $input) {
                uuid
                name
                facility_id
                location_id
                order_number
                warehouse_zone_uuid
                configuration
                created_at
                updated_at
              }
            }
          `,
          variables: {
            uuid: door.id,
            input: {
              configuration: JSON.stringify(updatedConfig)
            }
          }
        })
      });

      const result = await response.json();
      if (result.errors) {
        console.error('Error updating dock configuration:', result.errors);
        throw new Error(result.errors[0].message);
      }

      // Refresh data immediately after successful update
      setTimeInterval(prev => prev + 1);

    } catch (error) {
      console.error('Error updating dock paused status:', error);
    }
  };
  
  // Get color based on status
  const getStatusColor = (status: Status): string => {
    switch (status) {
      case 'empty':
        return 'bg-gray-500';
      case 'loading':
        return 'bg-yellow-500';
      case 'loaded':
        return 'bg-green-500';
      case 'unloading':
        return 'bg-blue-500';
      case 'unloaded':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };
  
  // Get color based on group
  const getGroupColor = (group: SpotGroup): string => {
    switch (group) {
      case 'inbound':
        return 'border-blue-500 border-4';
      case 'outbound':
        return 'border-orange-500 border-4';
      default:
        return 'border-gray-400 border';
    }
  };

  // Helper function to determine if a dock is in use (has trailers, shipments, or drivers)
  const isDockInUse = (door: DockDoor): boolean => {
    return !!(door.trailerNumber || door.referenceNumber || door.driverName || door.carrier_name);
  };

  // Helper function to determine if a dock is unavailable via configuration
  const isDockUnavailable = (door: DockDoor): boolean => {
    return door.configurationStatus === 'unavailable';
  };

  // Helper function to get door with pending changes applied
  const getDoorWithPendingChanges = (door: DockDoor): DockDoor => {
    const pendingChanges = pendingDockChanges[door.id];
    if (!pendingChanges) return door;
    
    return {
      ...door,
      ...pendingChanges
    };
  };

  // Helper function to get the effective status to display (configuration status takes precedence)
  const getEffectiveStatus = (door: DockDoor): string => {
    const doorWithChanges = getDoorWithPendingChanges(door);
    if (isDockUnavailable(doorWithChanges)) {
      return 'UNAVAILABLE';
    }
    if (door.paused) {
      return 'PAUSED';
    }
    return getDisplayStatus(doorWithChanges);
  };

  // Helper function to get text color for unavailable and paused docks
  const getCardTextColor = (door: DockDoor): string => {
    const doorWithChanges = getDoorWithPendingChanges(door);
    
    if (door.paused) {
      return '#374151'; // Gray-700 for readable text on gray background
    }
    
    if (isDockUnavailable(doorWithChanges)) {
      return '#6B7280'; // Gray-500 for muted text
    }
    
    return colorConfig.card.textColor;
  };

  // Helper function to determine if move CTAs should be disabled
  const shouldDisableMoveCTAs = (door: DockDoor): boolean => {
    // Disable if trailer status is not empty or loaded
    const trailerStatus = door.trailerStatus?.toLowerCase();
    if (trailerStatus !== 'empty' && trailerStatus !== 'loaded') {
      return true;
    }
    
    // Disable if trailer has an active lock (already being moved)
    if (door.trailerUuid) {
      const trailerHasLock = activeLocks.some((lock: any) => {
        const lockUuid = lock.resource_uuid || lock.resource?.uuid;
        return lockUuid === door.trailerUuid && 
               (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
      });
      
      if (trailerHasLock) {
        return true; // Disable - trailer is already being moved
      }
    }
    
    return false;
  };
  
  // Utility function to extract minutes from timer string (e.g., "01:30:00" -> 90)
  const getMinutesFromTimer = (timeString: string): number => {
    if (!timeString) return 0;
    
    // Handle negative times (starting with -)
    const isNegative = timeString.startsWith('-');
    const cleanTimeString = isNegative ? timeString.substring(1) : timeString;
    
    const parts = cleanTimeString.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      // If seconds are included, handle them too
      const seconds = parts.length > 2 ? (parseInt(parts[2], 10) || 0) / 60 : 0;
      
      return (hours * 60 + minutes + seconds) * (isNegative ? -1 : 1);
    }
    
    return 0;
  };

  // Helper function to format timer display values
  const formatTimerDisplay = (timeString: string, timerType?: 'mdt' | 'detention' | 'dockTimer'): string => {
    if (!timeString || timeString === '00:00:00') {
      // For MDT and detention, show N/A when there's no data
      if (timerType === 'mdt' || timerType === 'detention') {
        return 'N/A';
      }
      return '00:00:00';
    }
    
    // For MDT and detention: if negative (time ran out), show 00:00:00
    if ((timerType === 'mdt' || timerType === 'detention') && timeString.startsWith('-')) {
      return '00:00:00';
    }
    
    // For dock timer or when no negative: show absolute value (remove negative sign)
    if (timeString.startsWith('-')) {
      return timeString.substring(1);
    }
    
    return timeString;
  };

  // Sort dock doors based on the selected criteria and direction
  const getSortedDockDoors = (doors: DockDoor[]): DockDoor[] => {
    return [...doors].sort((a, b) => {
      switch (sortCriteria) {
        case 'order':
          // Sort by zone name alphabetically, then by order_number
          const zoneCompare = (a.zoneName || '').localeCompare(b.zoneName || '');
          if (zoneCompare !== 0) {
            return sortDirection === 'asc' ? zoneCompare : -zoneCompare;
          }
          // If same zone, sort by order_number
          const orderA = a.order_number || 0;
          const orderB = b.order_number || 0;
          return sortDirection === 'asc' ? orderA - orderB : orderB - orderA;
        
        case 'number':
          // Sort by door number
          const numA = parseInt(a.number.replace(/\D/g, ''), 10) || 0;
          const numB = parseInt(b.number.replace(/\D/g, ''), 10) || 0;
          return sortDirection === 'asc' ? numA - numB : numB - numA;
        
        case 'status':
          // Sort by most severe status first (based on timer statuses)
          const aWarnings = [
            getTimeStatus(a.mdt, 'mdt'), 
            'green', // detention always N/A so always green
            getTimeStatus(a.dock_timer || '00:00:00', 'dockTimer')
          ].filter(s => s === 'orange').length;
          
          const aCriticals = [
            getTimeStatus(a.mdt, 'mdt'), 
            'green', // detention always N/A so always green
            getTimeStatus(a.dock_timer || '00:00:00', 'dockTimer')
          ].filter(s => s === 'red').length;
          
          const bWarnings = [
            getTimeStatus(b.mdt, 'mdt'), 
            'green', // detention always N/A so always green
            getTimeStatus(b.dock_timer || '00:00:00', 'dockTimer')
          ].filter(s => s === 'orange').length;
          
          const bCriticals = [
            getTimeStatus(b.mdt, 'mdt'), 
            'green', // detention always N/A so always green
            getTimeStatus(b.dock_timer || '00:00:00', 'dockTimer')
          ].filter(s => s === 'red').length;
          
          // Compare criticals first, then warnings
          if (aCriticals !== bCriticals) {
            return sortDirection === 'desc' ? bCriticals - aCriticals : aCriticals - bCriticals;
          }
          return sortDirection === 'desc' ? bWarnings - aWarnings : aWarnings - bWarnings;
          
        case 'mdt':
          // For MDT and detention, negative values are worse
          const mdtA = a.mdt?.startsWith('-') ? -getMinutesFromTimer(a.mdt) : getMinutesFromTimer(a.mdt);
          const mdtB = b.mdt?.startsWith('-') ? -getMinutesFromTimer(b.mdt) : getMinutesFromTimer(b.mdt);
          return sortDirection === 'desc' ? mdtA - mdtB : mdtB - mdtA; // Descending sorts worst first
          
        case 'detention':
          // Detention is always N/A, so no sorting
          return 0;
          
        case 'dockTimer':
          // For dock timer, higher values are worse
          const dockA = getMinutesFromTimer(a.dock_timer);
          const dockB = getMinutesFromTimer(b.dock_timer);
          return sortDirection === 'desc' ? dockB - dockA : dockA - dockB; // Descending sorts worst first
          
        default:
          return 0;
      }
    });
  };
  
  // Get unique dock types from the API data
  const getUniqueDockTypes = (): DockType[] => {
    const uniqueTypes = new Set<DockType>();
    apiDockDoors.forEach(door => {
      uniqueTypes.add(door.dockType);
    });
    return Array.from(uniqueTypes).sort();
  };

  // Update the filteredDockDoors logic to include sorting
  const filteredAndSortedDockDoors = getSortedDockDoors(
    apiDockDoors.filter(door => {
      if (dockTypeFilter !== 'all' && door.dockType !== dockTypeFilter) return false;
      if (statusFilter !== 'all' && door.status !== statusFilter) return false;
      if (shipmentTypeFilter !== 'all' && door.shipment_type !== shipmentTypeFilter) return false;
      return true;
    })
  );
  
  // Doors with active assignments
  const activeDoors = apiDockDoors.filter(door => 
    door.status === 'loading' || door.status === 'unloading'
  );
  
  // Upcoming schedule based on scheduled times
  const upcomingSchedules = apiDockDoors.flatMap(door => 
    door.scheduledTimes.map(time => ({
      doorId: door.id,
      doorNumber: door.number,
      ...time
    }))
  ).sort((a, b) => a.start.localeCompare(b.start));
  
  // Add new helper functions at the top level
  const getTimeStatus = (timeString: string, timerType: 'mdt' | 'detention' | 'dockTimer'): 'red' | 'green' | 'orange' => {
    // For MDT and detention, if there's no data or it's 00:00:00, treat as green (N/A state)
    if ((timerType === 'mdt' || timerType === 'detention') && (!timeString || timeString === '00:00:00')) {
      return 'green';
    }
    
    const minutes = getMinutesFromTimer(timeString);
    const config = colorConfig[timerType];
    
    // For MDT and detention: check thresholds for both remaining time and overdue time
    // For dock timer: positive = elapsed time (higher = worse)
    if (timerType === 'mdt' || timerType === 'detention') {
      if (minutes > 0) {
        // Time remaining - check if we're approaching deadlines
        if (minutes <= config.criticalThreshold) {
          return 'red'; // Critical - very little time remaining
        } else if (minutes <= config.warningThreshold) {
          return 'orange'; // Warning - getting close to deadline
        }
        return 'green'; // Good - plenty of time remaining
      } else {
        // Overdue - always critical
        return 'red';
      }
    } else {
      // Dock timer logic (elapsed time, higher = worse)
      if (minutes >= config.criticalThreshold) {
        return 'red';
      } else if (minutes >= config.warningThreshold) {
        return 'orange';
      }
      return 'green';
    }
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  const handleDoorAction = async (door: DockDoor, action: DoorAction) => {
    // add a confirm box
    const confirm = window.confirm('Are you sure you want to perform this action?');
    if (!confirm) return;
    if (!door.referenceNumber) return;
    
    try {
      // Try GraphQL first, fallback to REST if needed
      const actionType = action.apiEndpoint.includes('send_to_yard') ? 'send_to_yard' :
                         action.apiEndpoint.includes('update_loading_status') ? 'update_loading_status' :
                         'unknown';
      
      if (actionType !== 'unknown') {
        await performGraphQLDockAction(door.id, actionType, {
          status: action.label === 'Safety Check Complete' ? 'safety_check_complete' :
                  action.label === 'Loading Complete' ? 'loading_complete' :
                  action.label === 'Unloading Complete' ? 'unloading_complete' :
                  typeof action.label === 'function' && action.label(door).includes('Start') ? 'loading_started' :
                  undefined
        });
      } else {
        // Fallback to existing REST API
        await axios.post(action.apiEndpoint, {
          shipment_id: door.id
        });
      }
      
      // Force a refresh by incrementing the time interval
      setTimeInterval(prev => prev + 1);
    } catch (error) {
      console.error('Failed to update door status:', error);
      // Fallback to existing REST API if GraphQL fails
      try {
        await axios.post(action.apiEndpoint, {
          shipment_id: door.id
        });
        setTimeInterval(prev => prev + 1);
      } catch (restError) {
        console.error('REST API also failed:', restError);
      }
    }
  };

  // Handle CTA button clicks for advancing trailer status
  const handleCTAClick = async (door: DockDoor, cta: CTAConfig) => {
    if (!door.trailerLineageUuid) {
      console.error('No trailer lineage UUID found for dock:', door.id);
      return;
    }

    await handleAdvanceStatus(door.trailerLineageUuid, cta.targetStatus);
  };

  // Check if a dock door has an active safety check lock
  const hasSafetyCheckLock = (door: DockDoor): boolean => {
    return activeLocks.some((lock: any) => {
      const lockUuid = lock.resource_uuid || lock.resource?.uuid;
      const isTrailerLock = lockUuid === door.trailerUuid;
      const isDockLock = lockUuid === door.id;
      return (isTrailerLock || isDockLock) && lock.namespace === 'safety_check';
    });
  };

  // Handle creating a standalone safety check
  const handleCreateSafetyCheck = async () => {
    if (!selectedDoor?.trailerUuid || !selectedFacility?.id) return;

    setCreatingSafetyCheck(true);
    setSafetyCheckError(null);

    try {
      const result = await createSafetyCheck({
        trailer_uuid: selectedDoor.trailerUuid,
        facility_id: String(selectedFacility.id),
        dock_uuid: selectedDoor.id || undefined,
        description: `Door ${selectedDoor.number} Safety Check`,
      });

      // Lock the door to show it's in a pending state (same as move tasks)
      setLockedDoors(prev => new Set(prev).add(selectedDoor.id));

      // Store task data and show success modal (same pattern as move tasks)
      setCreatedTask({
        uuid: result.uuid,
        task_type: { type: 'SAFETY_CHECK' },
        task_status: { status: 'UNASSIGNED' },
        trailer: { trailer_number: selectedDoor.trailerNumber },
      });
      setShowSafetyCheckConfirm(false);
      setShowDoorDetailsModal(false);
      setShowSuccessModal(true);

      // Force reload to show updated lock state
      setTimeInterval((prev: number) => prev + 1);
    } catch (error: any) {
      console.error('Failed to create safety check:', error);
      setSafetyCheckError(error.message || 'Failed to create safety check');
    } finally {
      setCreatingSafetyCheck(false);
    }
  };

  const handleOpenSafetyCheckConfirm = () => {
    if (!selectedDoor || hasSafetyCheckLock(selectedDoor)) return;
    setSafetyCheckError(null);
    setShowSafetyCheckConfirm(true);
  };

  // Yard data is now available from the combined useDockData hook above

  // Function to save configuration
  const saveConfiguration = async () => {
    try {
      setPendingFacilityChanges(prev => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          dock: {
            ...prev.configuration?.dock,
            colorConfig,
            lastUpdated: new Date().toISOString()
          }
        }
      }));
      setShowConfigModal(false);
    } catch (error) {
      console.error('Error saving configuration:', error);
    }
  };

  // Function to get timer background color based on status and configuration
  const getTimerBackgroundColor = (type: 'mdt' | 'detention' | 'dockTimer', status: 'red' | 'green' | 'orange', door?: DockDoor) => {
    // If door is provided and is paused, return gray color instead of status-based color
    if (door && door.paused) {
      return '#9CA3AF'; // Gray-400 - readable gray for timers
    }
    
    const config = colorConfig[type];
    return status === 'red' ? config.critical : status === 'orange' ? config.warning : config.good;
  };

  // Function to get timer text color
  const getTimerTextColor = (type: 'mdt' | 'detention' | 'dockTimer', door?: DockDoor) => {
    // If door is provided and is paused, return dark gray for readability on gray background
    if (door && door.paused) {
      return '#1F2937'; // Gray-800 - dark text for readability
    }
    
    const config = colorConfig[type];
    return config.textColor;
  };

  // Function to get card background color based on status indicators
  const getCardBackgroundColor = (door: DockDoor) => {
    const doorWithChanges = getDoorWithPendingChanges(door);
    
    // If dock is paused, always show gray (overrides all other status colors)
    if (door.paused) {
      return '#D1D5DB'; // Gray-300
    }
    
    // If dock is unavailable via configuration, show gray
    if (isDockUnavailable(doorWithChanges)) {
      return '#D1D5DB'; // Gray-300
    }

    // Only apply timer-based coloring for docks that are in use
    if (!isDockInUse(doorWithChanges)) {
      return colorConfig.card.default;
    }

    const statuses = [
      getTimeStatus(doorWithChanges.mdt, 'mdt'),
      'green', // detention always N/A so always green
      getTimeStatus(doorWithChanges.dock_timer, 'dockTimer')
    ];
    
    const warningCount = statuses.filter(status => status === 'orange').length;
    const criticalCount = statuses.filter(status => status === 'red').length;

    if (criticalCount === 3) return colorConfig.card.threeCriticals;
    if (criticalCount === 2) return colorConfig.card.twoCriticals;
    if (criticalCount === 1) return colorConfig.card.oneCritical;
    if (warningCount === 3) return colorConfig.card.threeWarnings;
    if (warningCount === 2) return colorConfig.card.twoWarnings;
    if (warningCount === 1) return colorConfig.card.oneWarning;
    if (warningCount > 0 || criticalCount > 0) return colorConfig.card.mixed;
    return colorConfig.card.default;
  };

  // Function to get card border style for dock doors
  const getCardBorderStyle = (door: DockDoor, isHighlighted: boolean) => {
    const doorWithChanges = getDoorWithPendingChanges(door);
    
    // If dock is paused, use gray borders (override highlight and direction colors)
    if (door.paused) {
      return isHighlighted ? '3px solid #6B7280' : '2px solid #9CA3AF'; // Gray-500/Gray-400
    }
    
    // Normal highlighting and direction-based borders
    if (isHighlighted) {
      return '3px solid #000000';
    }
    
    if (doorWithChanges.direction === 'inbound') {
      return '2px solid #3B82F6'; // Blue
    }
    
    if (doorWithChanges.direction === 'outbound') {
      return '2px solid #F97316'; // Orange
    }
    
    return '1px solid #9CA3AF'; // Default gray
  };

  // Add a handler for the modal tabs
  const handleConfigModalTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setConfigModalTab(newValue);
  };

  // Trailer lineage notes state
  const [trailerNotesValue, setTrailerNotesValue] = useState<string>('');
  const [isEditingTrailerNotes, setIsEditingTrailerNotes] = useState(false);
  const [savingTrailerNotes, setSavingTrailerNotes] = useState(false);

  // Shipment editing state
  const [newShipmentReference, setNewShipmentReference] = useState<string>('');
  const [shipmentReferenceError, setShipmentReferenceError] = useState<string>('');
  const [isUpdatingShipment, setIsUpdatingShipment] = useState<boolean>(false);
  const [showRemoveShipmentConfirm, setShowRemoveShipmentConfirm] = useState<boolean>(false);
  const [isRemovingShipment, setIsRemovingShipment] = useState<boolean>(false);



  // Function to save trailer lineage notes
  const handleSaveTrailerNotes = async () => {
    if (!selectedDoor || !selectedDoor.trailerUuid) return;
    
    setSavingTrailerNotes(true);
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // GraphQL mutation to update trailer notes
      const updateTrailerMutation = `
        mutation UpdateTrailer($uuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $uuid, input: $input) {
            uuid
            trailer_number
            active_lineage {
              uuid
              notes
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateTrailerMutation,
          variables: {
            uuid: selectedDoor.trailerUuid,
            input: {
              notes: trailerNotesValue
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Update the selected door with the new trailer notes immediately for UI feedback
      const updatedDoor = {...selectedDoor, trailerLineageNotes: trailerNotesValue};
      setSelectedDoor(updatedDoor);
      
      // Exit edit mode
      setIsEditingTrailerNotes(false);
      
      // Refresh data to ensure consistency
      setTimeInterval(prev => prev + 1);
      
    } catch (error: any) {
      console.error('Failed to save trailer notes:', error);
      alert('Failed to save trailer notes. Please try again.');
    } finally {
      setSavingTrailerNotes(false);
    }
  };

  // Validate shipment reference and get shipment ID
  const validateShipmentReference = async (referenceNumber: string): Promise<string | null> => {
    if (!referenceNumber.trim()) return null;

    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const shipmentValidationQuery = `
        query GetShipmentIdByPUID {
          shipment_without_trailer_by_reference_number(reference_number: "${referenceNumber.trim()}", facility_id: "${selectedFacility?.id || "1"}", trailer_uuid: "${selectedDoor?.trailerUuid}") {
            id
          }
        }
      `;

      const validationResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: shipmentValidationQuery
        })
      });

      const validationResult = await validationResponse.json();

      if (validationResult.errors) {
        throw new Error(validationResult.errors[0].message);
      }

      const shipmentId = validationResult.data?.shipment_without_trailer_by_reference_number?.id;
      
      if (!shipmentId) {
        throw new Error(`No unassigned shipment found with reference number: ${referenceNumber.trim()}`);
      }

      return shipmentId;
    } catch (err: any) {
      console.error('Error validating shipment reference:', err);
      throw err;
    }
  };

  // Handle update trailer shipment
  const handleUpdateTrailerShipment = async () => {
    if (!selectedDoor || !selectedDoor.trailerUuid) return;
    
    try {
      setIsUpdatingShipment(true);
      
      let shipmentId = null;
      
      // Validate shipment reference if provided
      if (newShipmentReference.trim()) {
        try {
          shipmentId = await validateShipmentReference(newShipmentReference);
          setShipmentReferenceError(''); // Clear any previous error
        } catch (validationError: any) {
          setShipmentReferenceError(validationError.message);
          setIsUpdatingShipment(false);
          return;
        }
      } else {
        setShipmentReferenceError(''); // Clear error if no reference number
      }
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      const updateTrailerMutation = `
        mutation UpdateTrailer($trailerUuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $trailerUuid, input: $input) {
            uuid
            trailer_number
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateTrailerMutation,
          variables: {
            trailerUuid: selectedDoor.trailerUuid,
            input: {
              shipmentId: shipmentId
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to show updated shipment
      setTimeInterval(prev => prev + 1);
      
      // Clear the input and close the modal
      setNewShipmentReference('');
      setShowDoorDetailsModal(false);
      setSelectedDoor(null);
      
    } catch (error: any) {
      console.error('Failed to update shipment:', error);
      setShipmentReferenceError(error.message || 'Failed to update shipment. Please try again.');
    } finally {
      setIsUpdatingShipment(false);
    }
  };

  // Helper function to determine if remove shipment button should be enabled
  const isRemoveShipmentButtonEnabled = (door: DockDoor | null) => {
    if (!door) return false;
    
    // Check if door has a shipment relationship
    if (!door.referenceNumber && !door.shipmentNumber) return false;
    
    // Enable button for dock doors - allow removal from dock
    return true;
  };

  // Function to remove shipment relationship
  const handleRemoveShipmentRelationship = async () => {
    if (!selectedDoor || !selectedDoor.trailerUuid) return;
    
    try {
      setIsRemovingShipment(true);
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      const removeShipmentMutation = `
        mutation RemoveShipmentFromTrailer($uuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $uuid, input: $input) {
            uuid
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: removeShipmentMutation,
          variables: {
            uuid: selectedDoor.trailerUuid,
            input: {
              shipmentId: null
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Close confirmation modal and refresh data
      setShowRemoveShipmentConfirm(false);
      setTimeInterval(prev => prev + 1);
      
    } catch (error: any) {
      console.error('Failed to remove shipment relationship:', error);
      alert('Failed to remove shipment relationship. Please try again.');
    } finally {
      setIsRemovingShipment(false);
    }
  };

  // Handle unified move to yard/zone modal close (exact same as YardSpotManagement)
  const handleCloseMoveToYardModal = () => {
    setShowMoveToYardModal(false);
    setSelectedDestinationType('yard_spot');
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setSelectedZoneFilter(null);
    setSpotFilter('');
    setSelectedFacilityZone('');
    setAutoCompleteMoveToYard(false);
    setMoveToDestinationError(null);
    setValidatingShipmentForYard(false);
    setMovingToDestination(false);
  };
  
  // Handle unified move to destination (yard spot or facility zone)
  const handleMoveToDestination = async () => {
    if (!selectedDoor) return;
    
    if (selectedDestinationType === 'yard_spot' && !selectedYardSpotUuid.trim()) {
      setMoveToDestinationError('Please select a yard spot');
      return;
    }
    
    if (selectedDestinationType === 'facility_zone' && !selectedFacilityZone.trim()) {
      setMoveToDestinationError('Please select a facility zone');
      return;
    }
    
    setMovingToDestination(true);
    setMoveToDestinationError(null);
    
    try {
      const authToken = getAuthToken();

      let shipmentId = null;
      
      // Validate shipment if reference number exists
      if (selectedShipmentreference_number.trim()) {
        setValidatingShipmentForYard(true);
        
        const shipmentValidationQuery = `
          query GetShipmentIdByPUID {
            shipment_without_trailer_by_reference_number(reference_number: "${selectedShipmentreference_number.trim()}", facility_id: "${selectedFacility?.id || "1"}", trailer_uuid: "${selectedDoor?.trailerUuid || ""}") {
              id
            }
          }
        `;

        const shipmentResponse = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: shipmentValidationQuery
          })
        });

        const shipmentResult = await shipmentResponse.json();
        
        if (shipmentResult.errors) {
          throw new Error(`Shipment validation failed: ${shipmentResult.errors[0].message}`);
        }

        if (shipmentResult.data.shipment_without_trailer_by_reference_number) {
          shipmentId = shipmentResult.data.shipment_without_trailer_by_reference_number.id;
        }
        
        setValidatingShipmentForYard(false);
      }

      const movementMutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $facility_zone_uuid: String,
          $yard_spot_uuid: String,
          $destination_facility_zone_uuid: String,
          $warehouse_zone_uuid: String,
          $dock_uuid: String,
          $driver_id: String,
          $shipment_id: String,
          $force: Boolean,
          $auto_complete: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            facility_zone_uuid: $facility_zone_uuid
            yard_spot_uuid: $yard_spot_uuid
            destination_facility_zone_uuid: $destination_facility_zone_uuid
            warehouse_zone_uuid: $warehouse_zone_uuid
            dock_uuid: $dock_uuid
            driver_id: $driver_id
            shipment_id: $shipment_id
            force: $force
            auto_complete: $auto_complete
          }) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
          }
        }
      `;

      const variables: any = {
        trailer_uuid: selectedDoor.trailerUuid,
        facility_id: selectedFacility?.id?.toString() || "1",
        warehouse_zone_uuid: undefined,
        dock_uuid: selectedDoor.id,
        driver_id: selectedDoor.assignedDriverId || undefined,
        shipment_id: shipmentId || undefined,
        force: false
      };

      if (selectedDestinationType === 'yard_spot') {
        variables.movement_type = 'warehouse_to_yard';
        variables.yard_spot_uuid = selectedYardSpotUuid;
        
        // Find the facility zone UUID for the selected yard spot
        const selectedSpot = yardSpots.find(spot => spot.id === selectedYardSpotUuid);
        if (selectedSpot) {
          variables.facility_zone_uuid = selectedSpot.zoneId;
        }
      } else {
        variables.movement_type = 'dock_to_facility_zone';
        variables.destination_facility_zone_uuid = selectedFacilityZone;
      }
      
      // Add auto_complete if checked
      if (autoCompleteMoveToYard) {
        variables.auto_complete = true;
      }

      console.log('Creating movement task with variables:', variables);

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: movementMutation,
          variables
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      console.log('Movement task created successfully:', result.data.create_movement_task);
      
      // Store the task data and show success modal
      setCreatedTask(result.data.create_movement_task);
      setShowSuccessModal(true);
      
      // Close the modal
      handleCloseMoveToYardModal();
      
      // Refresh data after a delay to show the updated state
      setTimeout(() => {
        setTimeInterval(prev => prev + 1);
      }, 2000);
      
    } catch (error: any) {
      console.error('Failed to create movement task:', error);
      setMoveToDestinationError(error.message || 'Failed to create movement task');
    } finally {
      setMovingToDestination(false);
      setValidatingShipmentForYard(false);
    }
  };

  // Function to cancel a task
  const handleCancelTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const cancelTaskMutation = `
        mutation CancelTask($taskUuid: String!) {
          cancel_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: cancelTaskMutation,
          variables: { taskUuid: taskId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      setTimeInterval(prev => prev + 1);
    } catch (error) {
      console.error('Failed to cancel task:', error);
      throw error;
    }
  };

  // Function to complete a task
  const handleCompleteTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const completeTaskMutation = `
        mutation CompleteTask($taskUuid: String!) {
          complete_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeTaskMutation,
          variables: { taskUuid: taskId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      setTimeInterval(prev => prev + 1);
    } catch (error) {
      console.error('Failed to complete task:', error);
      throw error;
    }
  };

  // Function to complete a movement task
  const handleCompleteMovementTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const completeMovementTaskMutation = `
        mutation CompleteMovementTask($input: CompleteMovementTaskInput!) {
          completeMovementTask(input: $input) {
            uuid
            taskStatus {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeMovementTaskMutation,
          variables: { 
            input: { 
              taskUuid: taskId 
            } 
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      setTimeInterval(prev => prev + 1);
    } catch (error) {
      console.error('Failed to complete movement task:', error);
      throw error;
    }
  };

  // Function to fetch and show task details from a lock
  const handleShowTaskFromLock = async (lock: any) => {
    if (!lock.task_uuid) {
      console.error('No task UUID in lock object');
      return;
    }

    try {
      // Get all locks for the dock door to find all related tasks
      const dockDoorId = lock.resource_uuid || lock.resource?.uuid;
      const allDoorLocks = dockDoorId ? getLocksForDockDoor(dockDoorId) : [lock];
      
      // Extract all task UUIDs from the locks
      const taskUuids = allDoorLocks
        .filter(doorLock => doorLock.task_uuid)
        .map(doorLock => doorLock.task_uuid);

      if (taskUuids.length === 0) {
        console.error('No task UUIDs found in locks');
        return;
      }

      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Fetch all tasks for the UUIDs
      const taskPromises = taskUuids.map(async (taskUuid) => {
        const taskQuery = `
          query GetTask($taskUuid: String!) {
            task(id: $taskUuid) {
              uuid
              data
              task_type {
                uuid
                type
              }
              task_status {
                uuid
                status
              }
              from_location
              to_location
              trailer {
                uuid
                trailer_number
              }
              created_at
              updated_at
            }
          }
        `;

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: taskQuery,
            variables: { taskUuid }
          })
        });

        const result = await response.json();
        if (result.data?.task) {
          return result.data.task;
        }
        return null;
      });

      const tasks = (await Promise.all(taskPromises)).filter(task => task !== null);

      if (tasks.length === 0) {
        // Fallback to original single task approach
        console.warn('Failed to fetch any tasks, falling back to single task');
        const fallbackTask = {
          uuid: lock.task_uuid,
          task_status: { status: 'ACTIVE' },
          task_type: { type: lock.operation_type || 'Movement Task' },
          from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource_uuid}`,
          to_location: 'Unknown',
          trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
          created_at: lock.created_at,
          data: {
            reason: lock.reason,
            operationType: lock.operation_type
          }
        };
        setSelectedTaskForDetails(fallbackTask);
        setShowTaskDetailsModal(true);
        return;
      }

      // Prioritize MOVE_TRAILER tasks - show MOVE_TRAILER if it exists, otherwise show first task
      const moveTrailerTask = tasks.find(task => 
        task.task_type?.type === 'MOVE_TRAILER' &&
        task.task_status?.status !== 'COMPLETED' && 
        task.task_status?.status !== 'CANCELLED'
      );
      
      const selectedTask = moveTrailerTask || tasks[0];
      setSelectedTaskForDetails(selectedTask);
      setShowTaskDetailsModal(true);

    } catch (error) {
      console.error('Error fetching task details:', error);
      // Show fallback task details from lock
      const fallbackTask = {
        uuid: lock.task_uuid,
        task_status: { status: 'ACTIVE' },
        task_type: { type: lock.operation_type || 'Movement Task' },
        from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource_uuid}`,
        to_location: 'Unknown',
        trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
        created_at: lock.created_at,
        data: {
          reason: lock.reason,
          operationType: lock.operation_type
        }
      };
      setSelectedTaskForDetails(fallbackTask);
      setShowTaskDetailsModal(true);
    }
  };

  // Function to handle moving trailers from dock to dock
  const handleMoveTrailerToDock = async () => {
    if (!selectedTargetDockDoor.trim()) {
      setMoveToDockError('Target dock door is required');
      return;
    }

    if (!selectedDoor) {
      setMoveToDockError('No source dock selected');
      return;
    }

    setMovingToDock(true);
    setMoveToDockError(null);
    
    let shipmentId = null;
    
    // Only validate reference number if one is provided
    if (selectedShipmentreference_number && selectedShipmentreference_number.trim()) {
      setValidatingShipment(true);
      
      try {
        // Get auth token from cookie or localStorage
        const authToken = getAuthToken();

        // Validate that the shipment reference number exists
        const shipmentValidationQuery = `
          query GetShipmentIdByreference_number {
            shipment_without_trailer_by_reference_number(reference_number: "${selectedShipmentreference_number.trim()}", facility_id: "${selectedFacility?.id || "1"}", trailer_uuid: "${selectedDoor?.trailerUuid || ""}") {
              id
              referenceNumber
              direction
              status
            }
          }
        `;

        const validationResponse = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: shipmentValidationQuery
          })
        });

        const validationResult = await validationResponse.json();
        
        if (validationResult.errors) {
          // Check if the error is "Shipment not found"
          const shipmentNotFoundError = validationResult.errors.find(
            (error: any) => error.message === "Shipment not found" && 
            error.path && error.path.includes("shipment_without_trailer_by_reference_number")
          );
          
          if (shipmentNotFoundError) {
            setMoveToDockError(`No shipment found with reference number "${selectedShipmentreference_number}". Please verify the reference number.`);
            return;
          }
          
          // For other errors, throw them
          throw new Error(validationResult.errors[0].message);
        }

        // Check if shipment was found
        const shipment = validationResult.data?.shipment_without_trailer_by_reference_number;
        if (!shipment || !shipment.id) {
          setMoveToDockError(`No shipment found with reference number "${selectedShipmentreference_number}". Please verify the reference number.`);
          return;
        }

        console.log('Shipment validation successful:', shipment);
        console.log('Extracted shipment ID:', shipment.id);
        shipmentId = shipment.id;
        setValidatingShipment(false);
      } catch (err) {
        console.error('Error validating shipment:', err);
        setMoveToDockError('Failed to validate reference number. Please try again.');
        setValidatingShipment(false);
        setMovingToDock(false);
        return;
      }
    }
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Find target dock door info using UUID for exact match
      const targetDoor = apiDockDoors.find(door => door.id === selectedTargetDockDoorId);
      
      if (!targetDoor) {
        throw new Error(`Target dock door with ID ${selectedTargetDockDoorId} not found`);
      }

      // GraphQL mutation for creating movement task from dock to dock
      const createMovementTaskMutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $warehouse_zone_uuid: String,
          $dock_uuid: String,
          $source_facility_zone_uuid: String,
          $destination_facility_zone_uuid: String,
          $shipment_id: String,
          $driver_id: String,
          $force: Boolean,
          $auto_complete: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            warehouse_zone_uuid: $warehouse_zone_uuid
            dock_uuid: $dock_uuid
            source_facility_zone_uuid: $source_facility_zone_uuid
            destination_facility_zone_uuid: $destination_facility_zone_uuid
            shipment_id: $shipment_id,
            driver_id: $driver_id,
            force: $force,
            auto_complete: $auto_complete
          }) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
          }
        }
      `;

             // Prepare variables for the mutation
       const variables: any = {
         trailer_uuid: selectedDoor.trailerUuid || selectedDoor.trailerNumber || selectedDoor.shipmentNumber || selectedDoor.id,
         movement_type: 'warehouse_to_warehouse',
         facility_id: selectedFacility?.id?.toString() || "1",
         warehouse_zone_uuid: targetDoor.warehouseZoneUuid || null,
         dock_uuid: targetDoor.id,
         source_facility_zone_uuid: null,
         destination_facility_zone_uuid: null,
         shipment_id: shipmentId || null,
         driver_id: selectedDoor?.assignedDriverId || null,
         force: false
       };
       
       // Add auto_complete if checked
       if (autoCompleteMoveToYard) {
         variables.auto_complete = true;
       }

      console.log('Creating dock-to-dock movement task with variables:', variables);
      console.log('Shipment ID being sent to movement task:', variables.shipment_id);

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: createMovementTaskMutation,
          variables
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(result.errors[0].message);
      }

      // Check if the movement task was created successfully
      if (result.data?.create_movement_task?.uuid) {
        console.log('Dock-to-dock movement task created successfully:', result.data.create_movement_task);
        
        // Lock the door to show it's in a pending state
        setLockedDoors(prev => new Set(prev).add(selectedDoor.id));
        
        // Store the task data and show success modal
        setCreatedTask(result.data.create_movement_task);
        setShowMoveToDockModal(false);
        setSelectedTargetDockDoor('');
        setSelectedTargetDockDoorId('');
        setDockMoveFilter('');
        setSelectedShipmentreference_number('');
        setAutoCompleteMoveToYard(false);
        setShowSuccessModal(true);
        
        // Refresh data after a delay to show the locked state
        setTimeout(() => {
          setTimeInterval(prev => prev + 1);
        }, 2000);
      } else {
        console.error('Failed to create dock-to-dock movement task:', result.data);
        throw new Error('Failed to create dock-to-dock movement task. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to move trailer to dock:', error);
      setMoveToDockError(error.message || 'Failed to move trailer to dock. Please try again.');
    } finally {
      setMovingToDock(false);
    }
  };

  // Add tiny mode state with localStorage persistence
  const [isTinyMode, setIsTinyMode] = useState(() => {
    const saved = localStorage.getItem('dockTinyMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Add state for tracking expanded card
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  
  // Track modal states to control data refresh timing
  useEffect(() => {
    const anyModalOpen = 
      showDoorDetailsModal ||
      showHistoryModal ||
      showYardMoveModal ||
      showMoveToYardModal ||
      showMoveToDockModal ||
      showSuccessModal ||
      showConfigModal ||
      showTaskDetailsModal ||
      showRemoveShipmentConfirm;
    
    setIsModalOpen(anyModalOpen);
  }, [
    showDoorDetailsModal,
    showHistoryModal,
    showYardMoveModal,
    showMoveToYardModal,
    showMoveToDockModal,
    showSuccessModal,
    showConfigModal,
    showTaskDetailsModal,
    showRemoveShipmentConfirm,
    setIsModalOpen
  ]);

  // Track active resource when a modal is open with a selected door
  useEffect(() => {
    const anyModalOpenWithDoor =
      (showDoorDetailsModal ||
       showMoveToYardModal ||
       showMoveToDockModal ||
       showRemoveShipmentConfirm) && selectedDoor;

    if (anyModalOpenWithDoor) {
      // Track the selected door as the active resource
      setActiveResourceIds(new Set([selectedDoor.id]));
    } else if (!isModalOpen) {
      // Clear active resources when no modals are open
      setActiveResourceIds(new Set());
    }
  }, [
    showDoorDetailsModal,
    showMoveToYardModal,
    showMoveToDockModal,
    showRemoveShipmentConfirm,
    selectedDoor,
    isModalOpen,
    setActiveResourceIds
  ]);

  // Save tiny mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('dockTinyMode', JSON.stringify(isTinyMode));
  }, [isTinyMode]);

  // Function to get display status with special logic for trailer ready
  const getDisplayStatus = (door: DockDoor): string => {
    const status = door.current_status?.toLowerCase();
    const direction = door.direction?.toLowerCase();
    
    // Inbound and LOADED = TRAILER READY
    if (direction === 'inbound' && status === 'loaded') {
      return 'TRAILER READY';
    }
    
    // Outbound and EMPTY = TRAILER READY  
    if (direction === 'outbound' && status === 'empty') {
      return 'TRAILER READY';
    }
    
    // Everything else is passthrough
    return door.current_status.toUpperCase();
  };

  if (apiLoading) {
    return (
      <div className="flex flex-col items-center mt-4 w-full">
        <CircularProgress />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 text-red-500">
        <h1 className="font-bold text-xl mb-3">Error</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="shadow bg-[#FBFBFB] p-3 mb-5">
        <div className="flex flex-col md:flex-row gap-3 mb-3">
          <div>
            <h1 className="font-bold text-xl mb-3">Dock Management</h1>
            <p>Monitor and manage dock doors, view current status and historical usage.</p>
          </div>
          <div className="flex gap-3 ml-auto items-center">
            <FormControlLabel
              control={
                <Switch
                  checked={isTinyMode}
                  onChange={(e) => setIsTinyMode(e.target.checked)}
                  color="primary"
                />
              }
              label="Compact View"
            />
            <Select
              options={formatFacilityOptions(facilities)}
              isSearchable={true}
              isClearable
              value={selectedFacility ? {
                value: selectedFacility.id,
                label: selectedFacility.name,
                facility: selectedFacility
              } : null}
              placeholder="Facilities"
              onChange={(selectedOption: any) => {
                setSelectedFacility(selectedOption?.facility || null);
              }}
              className="w-60"
            />
            <div className="w-40 flex items-center justify-center border rounded px-3 py-2 bg-gray-50">
              <span className="text-sm font-medium">Time: Max</span>
            </div>
            {getPendingChangesCount() > 0 && (
              <button 
                className={twMerge(
                  "px-3 py-2 rounded text-sm font-medium flex items-center",
                  savingChanges ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-500",
                  "text-white"
                )}
                onClick={saveAllChanges}
                disabled={savingChanges}
              >
                {savingChanges ? (
                  <>
                    <CircularProgress size={16} className="mr-2" color="inherit" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save fontSize="small" className="mr-1" />
                    Save Changes ({getPendingChangesCount()})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Current Status" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <div className="flex flex-col gap-5">
          {/* Filters Row */}
          <div className="flex flex-wrap gap-3 items-center bg-white p-3 shadow">
            <div className="flex items-center">
              <FilterList className="mr-2 text-gray-500" />
              <span className="font-medium mr-2">Filters:</span>
            </div>
            
            <div className="flex items-center mr-4">
              <span className="mr-2">Dock Type:</span>
              <Select
                options={[
                  { value: 'all', label: 'All Dock Types' },
                  ...getUniqueDockTypes().map(type => ({
                    value: type,
                    label: type.charAt(0).toUpperCase() + type.slice(1)
                  }))
                ]}
                defaultValue={{ value: 'all', label: 'All Dock Types' }}
                onChange={(option: any) => setDockTypeFilter(option.value)}
                className="w-40"
              />
            </div>

            <div className="flex items-center mr-4">
              <span className="mr-2">Shipment Type:</span>
              <Select
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'Drop', label: 'Drops' },
                  { value: 'Live', label: 'Lives' }
                ]}
                value={{ 
                  value: shipmentTypeFilter, 
                  label: shipmentTypeFilter === 'all' ? 'All Types' : 
                         shipmentTypeFilter === 'Drop' ? 'Drops' : 'Lives'
                }}
                onChange={(option: any) => setShipmentTypeFilter(option.value)}
                className="w-40"
              />
            </div>
            
            {/* <div className="flex items-center mr-4">
              <span className="mr-2">Status:</span>
              <Select
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'empty', label: 'Empty' },
                  { value: 'loading', label: 'Loading' },
                  { value: 'loaded', label: 'Loaded' },
                  { value: 'unloading', label: 'Unloading' },
                  { value: 'unloaded', label: 'Unloaded' }
                ]}
                defaultValue={{ value: 'all', label: 'All Status' }}
                onChange={(option: any) => setStatusFilter(option.value)}
                className="w-40"
              />
            </div> */}
            
            {/* Sort Options */}
            <div className="flex items-center mr-4">
              <span className="mr-2">Sort By:</span>
              <Select
                options={[
                  { value: 'order', label: 'Zone Order' },
                  { value: 'number', label: 'Door Number' },
                  { value: 'mdt', label: 'MDT' },
                  { value: 'detention', label: 'Detention Time' },
                  { value: 'dockTimer', label: 'Dock Timer' }
                ]}
                value={{ 
                  value: sortCriteria, 
                  label: sortCriteria === 'order' ? 'Zone Order' :
                         sortCriteria === 'number' ? 'Door Number' : 
                         sortCriteria === 'mdt' ? 'MDT' :
                         sortCriteria === 'detention' ? 'Detention Time' : 'Dock Timer'
                }}
                onChange={(option: any) => setSortCriteria(option.value)}
                className="w-40"
              />
            </div>
            
            <button
              className="p-2 rounded border flex items-center bg-gray-50 hover:bg-gray-100"
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            >
              {sortDirection === 'asc' ? (
                <>
                  <ArrowUpward fontSize="small" className="mr-1" />
                  <span>Ascending</span>
                </>
              ) : (
                <>
                  <ArrowDownward fontSize="small" className="mr-1" />
                  <span>Descending</span>
                </>
              )}
            </button>
          </div>
          
          {/* Dock Door Grid */}
          <div className={`grid ${isTinyMode ? 'grid-cols-8 sm:grid-cols-12 md:grid-cols-16 lg:grid-cols-20' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'} gap-2`}>
            {filteredAndSortedDockDoors.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500">
                No dock doors match your filters
              </div>
            ) : (
              filteredAndSortedDockDoors.map(door => {
                const isExpanded = expandedCardId === door.id;

                if (isTinyMode && isExpanded) {
                  // Expanded tiny card - Takes up more space in the grid but stays within the flow
                  return (
                    <div 
                      key={door.id}
                      className={`col-span-3 row-span-3 border rounded shadow-lg overflow-hidden flex flex-col h-auto bg-white z-10 ${mergeLockSets(taskBasedLockedDoors, lockedDoors).has(door.id) ? 'relative' : ''}`}
                      style={{ 
                        backgroundColor: getCardBackgroundColor(door),
                        color: getCardTextColor(door)
                      }}
                    >
                      {/* Gray overlay for locked doors */}
                      {mergeLockSets(taskBasedLockedDoors, lockedDoors).has(door.id) && (
                        <div 
                          className="absolute inset-0 bg-gray-500 bg-opacity-50 rounded flex items-center justify-center z-20 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (taskBasedLockedDoors.has(door.id)) {
                              const locks = getLocksForDockDoor(door.id);
                              if (locks.length > 0) {
                                handleShowTaskFromLock(locks[0]); // Show first lock if multiple
                              }
                            }
                          }}
                        >
                          <div className="bg-white px-3 py-1 rounded-full text-sm font-medium text-gray-700 shadow">
                            {taskBasedLockedDoors.has(door.id) ? 'Task Active - Click for details' : 'Task Created - Processing...'}
                          </div>
                        </div>
                      )}
                      {/* Zone Banner */}
                      {door.zoneName && (
                        <div className="bg-gray-600 text-white text-xs font-medium px-3 py-1 flex justify-between items-center">
                          <span>{door.zoneName}</span>
                          {/* Pause/Play button with circular background */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePausePlayToggle(door);
                            }}
                            className="dock-pause-button w-5 h-5 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                            title={door.paused ? 'Resume dock door' : 'Pause dock door'}
                          >
                            {door.paused ? 
                              <PlayArrow style={{ fontSize: '12px', color: 'white' }}/> : 
                              <Pause style={{ fontSize: '12px', color: 'white' }}/>
                            }
                          </button>
                        </div>
                      )}
                      
                      {/* Door Header */}
                      <div className="flex justify-between items-center p-3 border-b">
                        <h3 className="font-bold">Door {door.number}</h3>
                        <div className="flex items-center gap-2">
                          {/* Close button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); 
                              setExpandedCardId(null); 
                            }}
                            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                          >
                            <Close fontSize="small"/>
                          </button>
                          <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            {getEffectiveStatus(door)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Status Boxes - Only show for docks in use */}
                      {isDockInUse(door) && (
                        <div className="grid grid-cols-3 gap-1 p-2 border-b">
                          {/* MDT Box */}
                          <div style={{ 
                            backgroundColor: getTimerBackgroundColor('mdt', getTimeStatus(door.mdt, 'mdt'), door),
                            color: getTimerTextColor('mdt', door)
                          }} className="p-2 rounded border border-black">
                            <div className="text-xs font-medium mb-1">MDT</div>
                            <div className="text-sm">{formatTimerDisplay(door.mdt, 'mdt')}</div>
                          </div>

                          {/* Dock Timer Box */}
                          <div style={{ 
                            backgroundColor: getTimerBackgroundColor('dockTimer', getTimeStatus(door.dock_timer || '00:00:00', 'dockTimer'), door),
                            color: getTimerTextColor('dockTimer', door)
                          }} className="p-2 rounded border border-black">
                            <div className="text-xs font-medium mb-1">Dock Timer</div>
                            <div className="text-sm">{door.dock_timer || '00:00:00'}</div>
                          </div>

                          {/* Detention Time Box */}
                          <div style={{ 
                            backgroundColor: getTimerBackgroundColor('detention', 'green', door),
                            color: colorConfig.detention.textColor
                          }} className="p-2 rounded border border-black">
                            <div className="text-xs font-medium mb-1">Det. time</div>
                            <div className="text-sm">N/A</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Door Body */}
                      <div className="p-3">
                        <div className="flex justify-between mb-2">
                          {door.direction && (
                            <div className={`text-xs px-2 py-1 rounded ${
                              door.direction === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                              {formatDirection(door.direction)}
                            </div>
                          )}
                        </div>
                        
                        {(door.status === 'loading' || door.status === 'unloading' || door.trailerNumber || door.referenceNumber) && (
                          <div className="mt-2  pt-2">
                            <div className="flex">
                              <div className="flex-1 pr-2">
                                <div className="flex items-center mb-1">
                                  <span className="text-sm font-medium mr-1">Driver:</span>
                                  <span className="text-sm">{door.driverName || "No Driver"}</span>
                                </div>
                                {door.carrier_name && ( <div className="flex items-center mb-1"> <span className="text-sm font-medium mr-1">SCAC:</span> <span className="text-sm">{door.carrier_name}</span> </div> )}
                                {door.trailerNumber && ( <div className="flex items-center mb-1"> <span className="text-sm font-medium mr-1">Trailer:</span> <span className="text-sm">{door.trailerNumber}</span> </div> )}
                                {door.referenceNumber && ( <div className="flex items-center mb-1"> <span className="text-sm font-medium mr-1">Ref #:</span> <span className="text-sm">{door.referenceNumber}</span> </div> )}
                                {door.mdtActualTime && ( <div className="flex items-center mb-1"> <span className="text-sm font-medium mr-1">MDT:</span> <span className="text-sm">{formatMilitaryDateTime(door.mdtActualTime)}</span> </div> )}
                                {door.appointment_date && ( <div className="flex items-center"> <span className="text-sm font-medium mr-1">Appt:</span> <span className="text-sm">{formatMilitaryDateTime(door.appointment_date)}</span> </div> )}
                              </div>
                              <div className="flex-1 pl-2 flex flex-col">
                                {door.note && ( <div className="flex flex-col flex-grow"> <div className="mt-1 text-sm text-gray-600 italic overflow-auto max-h-20 flex-grow bg-yellow-50 p-1 rounded">{door.note}</div> </div> )}
                                {door.trailerLineageNotes && ( <div className="flex flex-col flex-grow"> <div className="mt-1 text-sm text-gray-600 italic overflow-auto max-h-20 flex-grow bg-green-50 p-1 rounded"><span className="text-xs text-green-700 font-medium">Trailer Notes:</span><br/>{door.trailerLineageNotes}</div> </div> )}
                                {door.notes && ( <div className="flex flex-col flex-grow"> <div className="mt-1 text-sm text-gray-600 italic overflow-auto max-h-20 flex-grow bg-amber-50 p-1 rounded"><span className="text-xs text-amber-700 font-medium">Dock Notes:</span><br/>{door.notes}</div> </div> )}
                              </div>
                            </div>
                          </div>
                        )}

                        {door.scheduledTimes.length > 0 && (
                          <div className="mt-3  pt-3">
                            <h4 className="font-medium text-sm mb-1">Next Scheduled:</h4>
                            <p className="text-sm">{door.scheduledTimes[0].start} - {door.scheduledTimes[0].end}</p>
                            <p className="text-xs text-gray-600">{door.scheduledTimes[0].shipmentId}</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions Footer */}
                      <div className="bg-gray-50 p-2 flex flex-col gap-2 mt-auto">
                        {/* Trailer Status Display */}
                        
                        
                        {/* CTA Button Row */}
                       
                        
                        {/* Move/Action Buttons Row - Only show if dock is not unavailable */}
                        {!isDockUnavailable(door) && (
                          <div className="flex gap-0.5">
                            {/* Show Move to Yard for docks that are in use */}
                            {isDockInUse(door) && (
                              <button 
                                className={`text-white text-xs py-1 px-2 rounded flex items-center justify-center h-[2.25rem] ${
                                  shouldDisableMoveCTAs(door) 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-purple-500 hover:bg-purple-600'
                                }`}
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  if (shouldDisableMoveCTAs(door)) return;
                                  setSelectedDoor(door); 
                                  setSelectedShipmentreference_number(door.referenceNumber || door.reference_number || '');
                                  setShowMoveToYardModal(true); 
                                }} 
                                title="Move to Yard or Zone"
                              >
                                {isTinyMode ? (
                                  <SwapHoriz fontSize="inherit" className="text-[10px]" />
                                ) : (
                                  <>
                                    <SwapHoriz fontSize="small" className="mr-0.5 flex-shrink-0" /> 
                                    <span className="truncate">To Yard</span>
                                  </>
                                )}

                              </button>
                            )}
                            {/* Show Move to Dock for docks that are in use */}
                            {isDockInUse(door) && (
                              <button 
                                className={`text-white text-xs py-1 px-2 rounded flex items-center justify-center h-[2.25rem] ${
                                  shouldDisableMoveCTAs(door) 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-orange-500 hover:bg-orange-600'
                                }`}
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  if (shouldDisableMoveCTAs(door)) return;
                                  setSelectedDoor(door); 
                                  setSelectedShipmentreference_number(door.referenceNumber || door.reference_number || '');
                                  setShowMoveToDockModal(true); 
                                }} 
                                title="Move to Dock"
                              >
                                {isTinyMode ? (
                                  <SwapHoriz fontSize="inherit" className="text-[10px]" />
                                ) : (
                                  <>
                                    <SwapHoriz fontSize="small" className="mr-0.5 flex-shrink-0" /> 
                                    <span className="truncate">To Dock</span>
                                  </>
                                )}
                              </button>
                            )}
                           {(() => {
                             // When dock is in use, disable CTA that would imply configuration changes (visual only)
                             const availableCTA = getAvailableCTAs(door);
                             if (!availableCTA) return <div className="invisible"></div>;
                             
                             const ctaVariantClasses = {
                               primary: 'bg-sky-600 hover:bg-sky-700',
                               success: 'bg-emerald-600 hover:bg-emerald-700',
                               warning: 'bg-amber-600 hover:bg-amber-700'
                             };
                             
                             return (
                                 <button 
                                   key={`cta-${availableCTA.targetStatus}`}
                                   className={`${ctaVariantClasses[availableCTA.variant]} text-white text-xs py-1 px-2 rounded flex items-center justify-center h-[2.25rem] cta-button ${isDockInUse(door) ? 'opacity-60 cursor-not-allowed' : ''}`}
                                   onClick={(e) => { 
                                     e.stopPropagation(); 
                                     if (!isDockInUse(door)) handleCTAClick(door, availableCTA); 
                                   }}
                                   disabled={advanceStatusLoading || isDockInUse(door)}
                                   title={isDockInUse(door) ? 'Occupied - configuration changes locked' : undefined}
                                 >
                                   <ArrowForward fontSize="small" className="mr-1" /> {advanceStatusLoading ? 'Processing...' : availableCTA.label}
                                 </button>
                             );
                           })()}
                           
                          </div>
                        )}
                        {/* Show notes for unavailable docks */}
                        {isDockUnavailable(door) && door.notes && (
                          <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <div className="text-xs font-medium text-yellow-800 mb-1">Notes:</div>
                            <div className="text-xs text-yellow-700">{door.notes}</div>
                          </div>
                        )}
                        <div className="flex justify-end">
                          <button 
                            className="text-blue-600 hover:text-blue-800 text-xs flex items-center"
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedDoor(door);
                                setTrailerNotesValue(door.trailerLineageNotes || '');
                                setIsEditingTrailerNotes(false);
                                setShowDoorDetailsModal(true);
                            }}
                          >
                            <Edit fontSize="small" className="mr-1" /> Details Modal
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                } else if (isTinyMode) {
                  // Regular tiny card (not expanded)
                  return (
                    <Tooltip 
                      key={door.id}
                      title={`${door.zoneName ? `${door.zoneName} - ` : ''}Door ${door.number} - ${getEffectiveStatus(door)}`}
                      arrow
                      placement="top"
                    >
                      <div 
                        id={`dock-door-${door.id}`}
                        className={`relative aspect-square ${isDockUnavailable(door) ? 'cursor-not-allowed' : 'cursor-pointer'} flex flex-col items-center justify-center ${mergeLockSets(taskBasedLockedDoors, lockedDoors).has(door.id) ? 'relative' : ''}`}
                        style={{ 
                          backgroundColor: getCardBackgroundColor(door),
                          color: getCardTextColor(door),
                          border: getCardBorderStyle(door, highlightedDoorUuid === door.id)
                        }}
                        onClick={() => !isDockUnavailable(door) && setExpandedCardId(door.id)}
                      >
                        {/* Overlay + banner when dock has trailer (config locked) */}
                        {/* {isDockInUse(door) && (
                          <>
                            <div className="absolute inset-0 bg-gray-200 bg-opacity-30 rounded" />
                            <div className="absolute top-1 right-1 text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-0.5">
                              Occupied - Config Locked
                            </div>
                          </>
                        )} */}
                        {/* Gray overlay for locked doors */}
                        {mergeLockSets(taskBasedLockedDoors, lockedDoors).has(door.id) && (
                          <div 
                            className="absolute inset-0 bg-gray-500 bg-opacity-60 flex items-center justify-center z-20 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (taskBasedLockedDoors.has(door.id)) {
                                const locks = getLocksForDockDoor(door.id);
                                if (locks.length > 0) {
                                  handleShowTaskFromLock(locks[0]); // Show first lock if multiple
                                }
                              }
                            }}
                          >
                            <div className="bg-white px-1 py-0.5 rounded text-xs font-medium text-gray-700 shadow">
                              {taskBasedLockedDoors.has(door.id) ? 'Active Task - Click for details' : 'Processing...'}
                            </div>
                          </div>
                        )}
                        {/* Shipment Type badge in top left corner */}
                        {door.shipment_type && (
                          <div className="absolute top-1 left-1 text-[8px] font-semibold px-1 py-0.5 rounded bg-black bg-opacity-70 text-white">
                            {door.shipment_type.toUpperCase()}
                          </div>
                        )}
                        {/* Pause/Play button for tiny cards - small circular button in top right */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePausePlayToggle(door);
                          }}
                          className="dock-pause-button absolute top-1 right-1 w-4 h-4 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                          title={door.paused ? 'Resume dock door' : 'Pause dock door'}
                        >
                          {door.paused ? 
                            <PlayArrow style={{ fontSize: '10px', color: 'white' }}/> : 
                            <Pause style={{ fontSize: '10px', color: 'white' }}/>
                          }
                        </button>
                        <div className="text-xs font-bold">
                          {door.number}
                        </div>
                        <div className="text-[10px] font-bold mt-1 text-center px-1">
                          {getEffectiveStatus(door)}
                        </div>
                        {/* Compact view additional info */}
                        {isDockInUse(door) && (
                          <div className="text-[9px] mt-1 text-center px-1 space-y-0.5">
                            {door.trailerNumber && (
                              <div className="truncate">
                                <span className="font-medium">Trailer:</span> {door.carrier_name}-{door.trailerNumber}
                              </div>
                            )}
                     
                            {door.referenceNumber && (
                              <div className="truncate">
                                <span className="font-medium">Ref #:</span> {door.referenceNumber}
                              </div>
                            )}
                            {door.mdtActualTime && (
                              <div className="truncate">
                                <span className="font-medium">MDT:</span> {formatMilitaryDateTime(door.mdtActualTime)}
                              </div>
                            )}
                            {door.appointment_date && (
                              <div className="truncate">
                                <span className="font-medium">Appt:</span> {formatMilitaryDateTime(door.appointment_date)}
                              </div>
                            )}
                            {door.driver?.truck?.truckNumber && (
                              <div className="truncate">
                                <span className="font-medium">Truck:</span> {door.driver?.truck?.truckNumber}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  );
                } else {
                  // Regular card in normal (non-tiny) mode
                  return (
                    <div 
                      key={door.id} 
                      id={`dock-door-${door.id}`}
                      className={`relative rounded shadow-sm overflow-hidden flex flex-col h-full ${isDockUnavailable(door) ? 'cursor-not-allowed' : 'cursor-pointer'} ${mergeLockSets(taskBasedLockedDoors, lockedDoors).has(door.id) ? 'relative' : ''}`}
                      style={{ 
                        backgroundColor: getCardBackgroundColor(door),
                        color: getCardTextColor(door),
                        border: getCardBorderStyle(door, highlightedDoorUuid === door.id)
                      }}
                      onClick={() => { 
                        if (!isDockUnavailable(door)) {
                          setSelectedDoor(door);
                          setTrailerNotesValue(door.trailerLineageNotes || '');
                          setIsEditingTrailerNotes(false);
                          setShowDoorDetailsModal(true); 
                        }
                      }}
                    >
                      {/* Overlay + banner when dock has trailer (config locked) */}
                      {/* {isDockInUse(door) && (
                        <>
                          <div className="absolute inset-0 bg-gray-200 bg-opacity-30" />
                          <div className="absolute top-1 right-1 text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-0.5">
                            Occupied - Config Locked
                          </div>
                        </>
                      )} */}
                      {/* Gray overlay for locked doors */}
                      {mergeLockSets(taskBasedLockedDoors, lockedDoors).has(door.id) && (
                        <div 
                          className="absolute inset-0 bg-gray-500 bg-opacity-50 rounded flex items-center justify-center z-20 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (taskBasedLockedDoors.has(door.id)) {
                              const locks = getLocksForDockDoor(door.id);
                              if (locks.length > 0) {
                                handleShowTaskFromLock(locks[0]); // Show first lock if multiple
                              }
                            }
                          }}
                        >
                          <div className="bg-white px-3 py-1 rounded-full text-sm font-medium text-gray-700 shadow">
                            {taskBasedLockedDoors.has(door.id) ? 'Active Task - Click for details' : 'Task Created - Processing...'}
                          </div>
                        </div>
                      )}
                    {/* Normal card content - unchanged */}
                    {/* Zone Banner */}
                    {door.zoneName && (
                      <div className="bg-gray-600 text-white text-xs font-medium px-3 py-1 flex justify-between items-center">
                        <span>{door.zoneName}</span>
                        {/* Pause/Play button with circular background */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePausePlayToggle(door);
                          }}
                          className="dock-pause-button w-5 h-5 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                          title={door.paused ? 'Resume dock door' : 'Pause dock door'}
                        >
                          {door.paused ? 
                            <PlayArrow style={{ fontSize: '12px', color: 'white' }}/> : 
                            <Pause style={{ fontSize: '12px', color: 'white' }}/>
                          }
                        </button>
                      </div>
                    )}
                    
                    {/* Door Header */}
                      <div className={`flex justify-between items-center p-3 `}>
                        <h3 className="font-bold">Door {door.number}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800`}>
                            {getEffectiveStatus(door)}
                          </span>
                        </div>
                      </div>
                      {/* Status Boxes - Only show for docks in use */}
                      {isDockInUse(door) && (
                        <div className="grid grid-cols-3 gap-1 p-2 ">
                           {/* MDT Box */}
                          <div style={{ 
                            backgroundColor: getTimerBackgroundColor('mdt', getTimeStatus(door.mdt, 'mdt'), door),
                            color: getTimerTextColor('mdt', door)
                          }} className="p-2 rounded border border-black">
                            <div className="text-xs font-medium mb-1">MDT</div>
                            <div className="text-sm">{formatTimerDisplay(door.mdt, 'mdt')}</div>
                          </div>
                           {/* Dock Timer Box */}
                          <div style={{ 
                            backgroundColor: getTimerBackgroundColor('dockTimer', getTimeStatus(door.dock_timer || '00:00:00', 'dockTimer'), door),
                            color: getTimerTextColor('dockTimer', door)
                          }} className="p-2 rounded border border-black">
                            <div className="text-xs font-medium mb-1">Dock Timer</div>
                            <div className="text-sm">{door.dock_timer || '00:00:00'}</div>
                          </div>
                           {/* Detention Time Box */}
                          <div style={{ 
                            backgroundColor: getTimerBackgroundColor('detention', 'green', door),
                            color: colorConfig.detention.textColor
                          }} className="p-2 rounded border border-black">
                            <div className="text-xs font-medium mb-1">Det. time</div>
                            <div className="text-sm">N/A</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Door Body */}
                      <div className="p-3">
                        <div className="flex justify-between mb-2">
                          {door.direction && (
                            <div className={`text-xs px-2 py-1 rounded ${ door.direction === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800' }`}>
                              {formatDirection(door.direction)}
                            </div>
                          )}
                        </div>
                        {(door.status === 'loading' || door.status === 'unloading' || door.trailerNumber || door.shipmentNumber) && (
                          <div className="mt-2  pt-2">
                            <div className="flex">
                              <div className="flex-1 pr-2">
                                <div className="flex items-center mb-1"><span className="text-sm font-medium mr-1">Driver:</span><span className="text-sm">{door.driverName || "No Driver"}</span></div>
                                {door.driver?.truck?.truckNumber && (<div className="flex items-center mb-1"><span className="text-sm font-medium mr-1">Truck:</span><span className="text-sm">{door.driver.truck.truckNumber}</span></div>)}
                                {door.carrier_name && (<div className="flex items-center mb-1"><span className="text-sm font-medium mr-1">SCAC:</span><span className="text-sm">{door.carrier_name}</span></div>)}
                                {door.trailerNumber && (<div className="flex items-center mb-1"><span className="text-sm font-medium mr-1">Trailer:</span><span className="text-sm">{door.trailerNumber}</span></div>)}
                                {door.referenceNumber && (<div className="flex items-center mb-1"><span className="text-sm font-medium mr-1">Ref #:</span><span className="text-sm">{door.referenceNumber}</span></div>)}
                                {door.shipment_type && ( <div className="flex items-center mb-1"> <span className="text-sm font-medium mr-1">Type:</span> <span className="text-sm">{door.shipment_type}</span> </div> )}
                                {door.mdtActualTime && ( <div className="flex items-center mb-1"> <span className="text-sm font-medium mr-1">MDT:</span> <span className="text-sm">{formatMilitaryDateTime(door.mdtActualTime)}</span> </div> )}
                                {door.appointment_date && ( <div className="flex items-center"> <span className="text-sm font-medium mr-1">Appt:</span> <span className="text-sm">{formatMilitaryDateTime(door.appointment_date)}</span> </div> )}
                              </div>
                              <div className="flex-1 pl-2 flex flex-col">
                                {door.note && (<div className="flex flex-col flex-grow"><div className="mt-1 text-sm text-gray-600 italic overflow-auto max-h-20 flex-grow bg-yellow-50 p-1 rounded">{door.note}</div></div>)}
                                {door.trailerLineageNotes && (<div className="flex flex-col flex-grow"><div className="mt-1 text-sm text-gray-600 italic overflow-auto max-h-20 flex-grow bg-green-50 p-1 rounded"><span className="text-xs text-green-700 font-medium">Trailer Notes:</span><br/>{door.trailerLineageNotes}</div></div>)}
                                {door.notes && (<div className="flex flex-col flex-grow"><div className="mt-1 text-sm text-gray-600 italic overflow-auto max-h-20 flex-grow bg-amber-50 p-1 rounded"><span className="text-xs text-amber-700 font-medium">Dock Notes:</span><br/>{door.notes}</div></div>)}
                              </div>
                            </div>
                          </div>
                        )}
                        {door.scheduledTimes.length > 0 && (
                          <div className="mt-3 border-t pt-3">
                            <h4 className="font-medium text-sm mb-1">Next Scheduled:</h4>
                            <p className="text-sm">{door.scheduledTimes[0].start} - {door.scheduledTimes[0].end}</p>
                            <p className="text-xs text-gray-600">{door.scheduledTimes[0].shipmentId}</p>
                          </div>
                        )}
                      </div>
                      {/* Actions Footer */}
                      <div className="bg-gray-50 p-2 flex flex-col gap-2 mt-auto">
                       
                        
                       
                        
                        {/* Move/Action Buttons Row - Only show if dock is not unavailable */}
                        {!isDockUnavailable(door) && (
                          <div className="grid grid-cols-3 gap-1">
                            {/* Show Move to Yard for docks that are in use */}
                            {isDockInUse(door) ? (
                              <button 
                                className={`text-white text-xs py-1 px-2 rounded flex items-center justify-center h-[2.25rem] ${
                                  shouldDisableMoveCTAs(door) 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-purple-500 hover:bg-purple-600'
                                }`}
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  if (shouldDisableMoveCTAs(door)) return;
                                  setSelectedDoor(door); 
                                  setSelectedShipmentreference_number(door.referenceNumber || door.reference_number || '');
                                  setShowMoveToYardModal(true); 
                                }}
                                disabled={shouldDisableMoveCTAs(door)}
                              >
                                <SwapHoriz fontSize="small" className="mr-1" /> Move To Yard
                              </button>
                            ) : <div className="invisible"></div>}
                            {/* Zone functionality now integrated into single To Yard button */}
                            {/* Show Move to Dock for docks that are in use */}
                            {isDockInUse(door) ? (
                              <button 
                                className={`text-white text-xs py-1 px-2 rounded flex items-center justify-center h-[2.25rem] ${
                                  shouldDisableMoveCTAs(door) 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-orange-500 hover:bg-orange-600'
                                }`}
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  if (shouldDisableMoveCTAs(door)) return;
                                  setSelectedDoor(door); 
                                  setSelectedShipmentreference_number(door.referenceNumber || door.reference_number || '');
                                  setShowMoveToDockModal(true); 
                                }}
                                disabled={shouldDisableMoveCTAs(door)} >
                                <SwapHoriz fontSize="small" className="mr-1" /> Move To Dock
                              </button>
                            ) : <div className="invisible"></div>}
                              {/* CTA Button Row */}
                           {(() => {
                             const availableCTA = getAvailableCTAs(door);
                             if (!availableCTA) return <div className="invisible"></div>;
                             
                             const ctaVariantClasses = {
                               primary: 'bg-sky-600 hover:bg-sky-700',
                               success: 'bg-emerald-600 hover:bg-emerald-700',
                               warning: 'bg-amber-600 hover:bg-amber-700'
                             };
                             
                             return (
                                 <button 
                                   key={`cta-${availableCTA.targetStatus}`}
                                   className={`${ctaVariantClasses[availableCTA.variant]} text-white text-xs py-1 px-2 rounded flex items-center justify-center h-[2.25rem] cta-button`}
                                   onClick={(e) => { 
                                     e.stopPropagation(); 
                                     handleCTAClick(door, availableCTA); 
                                   }}
                                   disabled={advanceStatusLoading}
                                 >
                                  <ArrowForward fontSize="small" className="mr-1" /> {advanceStatusLoading ? 'Processing...' : availableCTA.label}
                                 </button>
                             );
                           })()}
                          </div>
                        )}
                        {/* Show notes for unavailable docks */}
                        {isDockUnavailable(door) && door.notes && (
                          <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <div className="text-xs font-medium text-yellow-800 mb-1">Notes:</div>
                            <div className="text-xs text-yellow-700">{door.notes}</div>
                          </div>
                        )}
                        
                      </div>
                    </div>
                  );
                }
              })
            )}
          </div>
          
        
        </div>
      )}

      {tabValue === 1 && (
        <div className="flex flex-col gap-5">
          {/* History Filters */}
          <div className="flex flex-wrap gap-3 items-center bg-white p-3 shadow">
            <div className="flex items-center">
              <FilterList className="mr-2 text-gray-500" />
              <span className="font-medium mr-2">Time Range:</span>
            </div>
            
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={(event, newTimeRange) => {
                if (newTimeRange !== null) {
                  setTimeRange(newTimeRange);
                }
              }}
              size="small"
            >
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>
            
            <div className="flex items-center ml-auto">
              <span className="mr-2">Door:</span>
              <Select
                options={[
                  { value: 'all', label: 'All Doors' },
                  ...apiDockDoors.map((door: DockDoor) => ({
                    value: door.id,
                    label: `Door ${door.number}`
                  }))
                ]}
                defaultValue={{ value: 'all', label: 'All Doors' }}
                className="w-40"
              />
            </div>
          </div>
          
          {/* Activity Timeline */}
          <div className="bg-white p-4 shadow">
            <h2 className="font-bold text-lg mb-4">Activity Timeline</h2>
            
            <div className="relative overflow-x-auto" style={{ height: '300px' }}>
              {/* Time markers */}
              <div className="absolute top-0 left-0 right-0 flex ">
                {Array.from({ length: 12 }, (_, i) => (
                  <div key={i} className="flex-1 text-xs text-center border-r py-1">
                    {(i + 8) % 12 === 0 ? '12' : (i + 8) % 12}:00 {i + 8 < 12 ? 'AM' : 'PM'}
                  </div>
                ))}
              </div>
              
              {/* Swim lanes for dock doors */}
              <div className="mt-8">
                {apiDockDoors.slice(0, 10).map((door, index) => {
                  const doorHistory = apiDockHistory[door.id] || [];
                  
                  return (
                    <div key={door.id} className="flex items-center h-10">
                      <div className="w-16 text-xs font-bold">
                        Door {door.number}
                      </div>
                      <div className="flex-1 relative h-6 bg-gray-100">
                        {doorHistory.map((entry, entryIndex) => {
                          // For demo purposes, position entries evenly across the timeline
                          const startPercent = (entryIndex * 20) % 80;
                          const widthPercent = 15;
                          
                          return (
                            <Tooltip 
                              key={entryIndex} 
                              title={`${entry.driverName} - ${entry.activity} (${entry.duration} min)`} 
                              arrow
                            >
                              <div 
                                className={`absolute h-full text-white text-xs flex items-center justify-center overflow-hidden whitespace-nowrap ${
                                  entry.activity === 'load' ? 'bg-orange-400' :
                                  entry.activity === 'unload' ? 'bg-blue-400' :
                                  'bg-purple-400'
                                }`}
                                style={{ 
                                  left: `${startPercent}%`,
                                  width: `${widthPercent}%`
                                }}
                              >
                                {entry.duration}m
                              </div>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 shadow">
              <h3 className="font-bold text-lg mb-3">Door Utilization</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Inbound Doors:</span>
                  <span className="font-bold">{apiDockDoors.filter(d => d.group === 'inbound').length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Outbound Doors:</span>
                  <span className="font-bold">{apiDockDoors.filter(d => d.group === 'outbound').length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Currently Active:</span>
                  <span className="font-bold">{activeDoors.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Utilization Rate:</span>
                  <span className="font-bold">
                    {apiDockDoors.length > 0 ? Math.round((activeDoors.length / apiDockDoors.length) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 shadow">
              <h3 className="font-bold text-lg mb-3">Activity Types</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Load Operations:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().filter(e => e.activity === 'load').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Unload Operations:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().filter(e => e.activity === 'unload').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Combined Operations:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().filter(e => e.activity === 'both').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Total Operations:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().length}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 shadow">
              <h3 className="font-bold text-lg mb-3">Duration Stats</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Avg. Dock Time:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().length > 0 
                      ? Math.round(Object.values(apiDockHistory).flat().reduce((sum, entry) => sum + entry.duration, 0) / 
                          Object.values(apiDockHistory).flat().length)
                      : 0} min
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Max Dock Time:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().length > 0
                      ? Math.max(...Object.values(apiDockHistory).flat().map(e => e.duration))
                      : 0} min
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Min Dock Time:</span>
                  <span className="font-bold">
                    {Object.values(apiDockHistory).flat().length > 0
                      ? Math.min(...Object.values(apiDockHistory).flat().map(e => e.duration))
                      : 0} min
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Door Details Modal */}
      {showDoorDetailsModal && selectedDoor && (
        <Modal
          open={showDoorDetailsModal}
          onClose={handleCloseDetailsModal}
          className="flex justify-center items-center"
        >
          <div className="w-[600px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
            {/* Stale data overlay - only show if THIS specific door has changed */}
            {hasStagedDataForResource(selectedDoor.id) && (
              <div className="absolute inset-0 bg-gray-900 bg-opacity-60 z-50 flex items-center justify-center rounded">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full">
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                    Data Updated
                  </h3>
                  <p className="text-sm text-center text-gray-600 mb-4">
                    This information has been updated. Please close and reopen this modal to see the latest data.
                  </p>
                  <button
                    onClick={handleCloseDetailsModal}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                  >
                    Close Modal
                  </button>
                </div>
              </div>
            )}
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={handleCloseDetailsModal}
            />
            <h1 className="text-xl font-bold mb-5">Door {selectedDoor.number} Details</h1>
            
            {/* <div className="mb-4">
              <h2 className="font-bold mb-2">Status</h2>
              <div className="flex gap-2">
                {(['empty', 'loading', 'loaded', 'unloading', 'unloaded'] as Status[]).map(status => (
                  <button
                    key={status}
                    className={twMerge(
                      'px-3 py-1 rounded text-sm',
                      selectedDoor.status === status 
                        ? `${getStatusColor(status)} text-white` 
                        : 'bg-gray-200 hover:bg-gray-300'
                    )}
                    onClick={() => handleStatusChange(status)}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div> */}
            
         
            
            <div className="mb-4">
              {/* Trailer Status Display */}
              {isDockInUse(selectedDoor) && selectedDoor.trailerStatus && (
                <div className="mb-3 border border-gray-300 rounded p-3 bg-white">
                  <span className="text-sm font-medium text-gray-700">Trailer Status: </span>
                  <span className="text-sm text-gray-900">{selectedDoor.trailerStatus}</span>
                </div>
              )}
              
              {/* CTA Button */}
              {(() => {
                const availableCTA = getAvailableCTAs(selectedDoor);
                if (!availableCTA) return null;
                
                const ctaVariantClasses = {
                  primary: 'bg-sky-600 hover:bg-sky-700',
                  success: 'bg-emerald-600 hover:bg-emerald-700',
                  warning: 'bg-amber-600 hover:bg-amber-700'
                };
                
                return (
                  <div className="mb-3">
                    <button 
                      className={`w-full ${ctaVariantClasses[availableCTA.variant]} text-white text-sm py-2 px-4 rounded flex items-center justify-center cta-button`}
                      onClick={() => handleCTAClick(selectedDoor, availableCTA)}
                      disabled={advanceStatusLoading}
                    >
                      {advanceStatusLoading ? 'Processing...' : availableCTA.label}
                    </button>
                  </div>
                );
              })()}
              
              <div className="flex items-center justify-between mb-2">
                {/* Move CTAs - show when door is in use */}
                {isDockInUse(selectedDoor) && (
                  <div className="flex gap-2">
                    <button 
                      className={`text-white text-sm py-1 px-3 rounded flex items-center ${
                        shouldDisableMoveCTAs(selectedDoor) 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-purple-500 hover:bg-purple-600'
                      }`}
                      onClick={() => {
                        if (shouldDisableMoveCTAs(selectedDoor)) return;
                        setSelectedShipmentreference_number(selectedDoor.referenceNumber || selectedDoor.reference_number || '');
                        setShowMoveToYardModal(true);
                      }}
                      disabled={shouldDisableMoveCTAs(selectedDoor)}
                    >
                      <SwapHoriz fontSize="small" className="mr-1" />
                      Move to Yard
                    </button>
                    <button 
                      className={`text-white text-sm py-1 px-3 rounded flex items-center ${
                        shouldDisableMoveCTAs(selectedDoor) 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-orange-500 hover:bg-orange-600'
                      }`}
                      onClick={() => {
                        if (shouldDisableMoveCTAs(selectedDoor)) return;
                        setSelectedShipmentreference_number(selectedDoor.referenceNumber || selectedDoor.reference_number || '');
                        setShowMoveToDockModal(true);
                      }}
                      disabled={shouldDisableMoveCTAs(selectedDoor)}
                    >
                      <SwapHoriz fontSize="small" className="mr-1" />
                      Move to Dock
                    </button>
                    {selectedDoor.trailerUuid && (
                      <button
                        className={`text-white text-sm py-1 px-3 rounded flex items-center ${
                          hasSafetyCheckLock(selectedDoor)
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-teal-600 hover:bg-teal-700'
                        }`}
                        onClick={handleOpenSafetyCheckConfirm}
                        disabled={hasSafetyCheckLock(selectedDoor)}
                      >
                        {hasSafetyCheckLock(selectedDoor) ? 'Safety Check Active' : 'Add Safety Check'}
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {selectedDoor.trailerNumber ? (
                <div className="grid grid-cols-2 gap-4">
                  {/* Driver Information */}
                  <div className="p-3 border rounded bg-blue-50">
                    <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      Driver Information
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">
                      Name: {selectedDoor.driverName || "No Driver"}
                    </p>
                    {selectedDoor.driverCheckInTime && (
                      <p className="text-sm text-gray-600">
                        Check-in Time: {formatMilitaryDateTime(selectedDoor.driverCheckInTime)}
                      </p>
                    )}
                    {selectedDoor.driver?.truck?.truckNumber && (
                      <p className="text-sm text-gray-600">
                        Truck: {selectedDoor.driver?.truck?.truckNumber}
                      </p>
                    )}
                  </div>

                  {/* Trailer Information */}
                  <div className="p-3 border rounded bg-green-50">
                    <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                        <path d="M3 4a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 14.846 4.632 16.5 6.414 16.5H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 6H6.28l-.31-1.243A1 1 0 005 4H3z"/>
                      </svg>
                      Trailer Information
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">
                      Number: {selectedDoor.trailerNumber}
                    </p>
                    {selectedDoor.carrier_name && (
                      <p className="text-sm text-gray-600 mb-1">
                        SCAC: {selectedDoor.carrier_name}
                      </p>
                    )}
                    {selectedDoor.trailerStatus && (
                      <p className="text-sm text-gray-600 mb-2">
                        Status: {selectedDoor.trailerStatus}
                      </p>
                    )}
                    
                    {/* Trailer Notes Section */}
                    {selectedDoor.trailerUuid && (
                      <div className="border-t pt-2 mt-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-700">Notes</span>
                          {!isEditingTrailerNotes && (
                            <button
                              className="text-sky-600 text-xs hover:underline flex items-center"
                              onClick={() => setIsEditingTrailerNotes(true)}
                            >
                              <Edit fontSize="small" className="mr-1" style={{fontSize: '12px'}} />
                              {selectedDoor.trailerLineageNotes ? 'Edit' : 'Add'}
                            </button>
                          )}
                        </div>
                        
                        {isEditingTrailerNotes ? (
                          <div>
                            <textarea
                              value={trailerNotesValue}
                              onChange={(e) => setTrailerNotesValue(e.target.value)}
                              placeholder="Enter notes about this trailer..."
                              className="w-full p-2 border rounded h-16 mb-2 text-xs"
                              disabled={savingTrailerNotes}
                            />
                            <div className="flex justify-end gap-2">
                              <button
                                className="text-gray-600 hover:text-gray-800 text-xs"
                                onClick={() => {
                                  setTrailerNotesValue(selectedDoor.trailerLineageNotes || '');
                                  setIsEditingTrailerNotes(false);
                                }}
                                disabled={savingTrailerNotes}
                              >
                                Cancel
                              </button>
                              <button
                                className="text-green-600 hover:text-green-800 text-xs flex items-center"
                                onClick={handleSaveTrailerNotes}
                                disabled={savingTrailerNotes}
                              >
                                {savingTrailerNotes ? (
                                  <>
                                    <CircularProgress size={10} className="mr-1" />
                                    Saving...
                                  </>
                                ) : (
                                  'Save'
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-2 border rounded bg-white">
                            {selectedDoor.trailerLineageNotes ? (
                              <p className="text-xs text-gray-600">{selectedDoor.trailerLineageNotes}</p>
                            ) : (
                              <p className="text-xs text-gray-500 italic">No notes added</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Shipment Information */}
                  <div className="p-3 border rounded bg-yellow-50">
                    <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z" clipRule="evenodd" />
                      </svg>
                      Shipment Information
                    </h3>
                    
                    {/* Current shipment info */}
                    {(selectedDoor.referenceNumber || selectedDoor.shipmentNumber) && (
                      <p className="text-sm text-gray-600 mb-2">
                        Current Reference: {selectedDoor.referenceNumber || selectedDoor.shipmentNumber}
                      </p>
                    )}

                    {selectedDoor.shipment_type && (
                      <p className="text-sm text-gray-600 mb-1">
                        Type: {selectedDoor.shipment_type}
                      </p>
                    )}
                    {selectedDoor.appointment_date && (
                      <p className="text-sm text-gray-600 mb-1">
                        Appointment: {formatMilitaryDateTime(selectedDoor.appointment_date)}
                      </p>
                    )}
                    {selectedDoor.mdtActualTime && (
                      <p className="text-sm text-gray-600 mb-1">
                        Must Depart By: {formatMilitaryDateTime(selectedDoor.mdtActualTime)}
                      </p>
                    )}
                    {selectedDoor.detentionActualTime && (
                      <p className="text-sm text-gray-600 mb-2">
                        Detention Time: {formatMilitaryDateTime(selectedDoor.detentionActualTime)}
                      </p>
                    )}

                    {/* Editable shipment reference section */}
                    {selectedDoor.trailerUuid && (
                      <div className="mt-3 pt-3 border-t border-yellow-200">
                        <p className="text-xs text-gray-600 mb-2">
                          Update Shipment Reference:
                        </p>
                        {shipmentReferenceError && (
                          <div className="mb-2 p-2 bg-red-100 rounded">
                            <p className="text-xs text-red-700">
                              {shipmentReferenceError}
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <TextField
                            fullWidth
                            size="small"
                            value={newShipmentReference}
                            onChange={(e) => {
                              setNewShipmentReference(e.target.value);
                              if (shipmentReferenceError) {
                                setShipmentReferenceError('');
                              }
                            }}
                            placeholder="Enter shipment reference number"
                            variant="outlined"
                            error={!!shipmentReferenceError}
                            sx={{ 
                              backgroundColor: 'white',
                              '& .MuiInputBase-input': {
                                fontSize: '0.875rem',
                                padding: '8px'
                              }
                            }}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleUpdateTrailerShipment}
                            disabled={!newShipmentReference.trim() || isUpdatingShipment}
                            startIcon={isUpdatingShipment ? <CircularProgress size={16} /> : <Save />}
                            sx={{ 
                              fontSize: '0.75rem',
                              minWidth: '80px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {isUpdatingShipment ? 'Updating...' : 'Update'}
                          </Button>
                        </div>
                        {(selectedDoor.referenceNumber || selectedDoor.shipmentNumber) && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => setShowRemoveShipmentConfirm(true)}
                            disabled={!isRemoveShipmentButtonEnabled(selectedDoor)}
                            sx={{
                              fontSize: '0.75rem',
                              opacity: !isRemoveShipmentButtonEnabled(selectedDoor) ? 0.5 : 1,
                              mt: 1
                            }}
                          >
                            Remove Shipment Relationship
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Dock Information */}
                  <div className="p-3 border rounded bg-purple-50">
                    <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
                      </svg>
                      Dock Information
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">
                      Status: {selectedDoor.current_status || 'Available'}
                    </p>
                    {selectedDoor.zoneName && (
                      <p className="text-sm text-gray-600 mb-1">
                        Zone: {selectedDoor.zoneName}
                      </p>
                    )}
                    {selectedDoor.dock_timer && selectedDoor.dock_timer !== '00:00:00' && (
                      <p className="text-sm text-gray-600">
                        Dock Timer: {formatTimerDisplay(selectedDoor.dock_timer, 'dockTimer')}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No current assignment</p>
              )}
            </div>
            
            
            

            
            {/* Dock Notes */}
            {selectedDoor.notes && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
                <p className="text-xs font-medium text-amber-800 mb-1">Dock Notes</p>
                <p className="text-sm text-amber-700">{selectedDoor.notes}</p>
              </div>
            )}

            <div className="mt-5 pt-3 border-t flex justify-end">
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
                onClick={handleCloseDetailsModal}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedHistoryDoor && (
        <Modal
          open={showHistoryModal}
          onClose={handleCloseHistoryModal}
          className="flex justify-center items-center"
        >
          <div className="w-[600px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
            {/* Stale data overlay - only show if THIS specific door has changed */}
            {hasStagedDataForResource(selectedHistoryDoor.id) && (
              <div className="absolute inset-0 bg-gray-900 bg-opacity-60 z-50 flex items-center justify-center rounded">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full">
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                    Data Updated
                  </h3>
                  <p className="text-sm text-center text-gray-600 mb-4">
                    This information has been updated. Please close and reopen this modal to see the latest data.
                  </p>
                  <button
                    onClick={handleCloseHistoryModal}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                  >
                    Close Modal
                  </button>
                </div>
              </div>
            )}
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={handleCloseHistoryModal}
            />
            <h1 className="text-xl font-bold mb-5">
              Door {selectedHistoryDoor.number} - Historical Activity
            </h1>
            
            <div className="mb-4">
              <ToggleButtonGroup
                value={timeRange}
                exclusive
                onChange={(event, newTimeRange) => {
                  if (newTimeRange !== null) {
                    setTimeRange(newTimeRange);
                  }
                }}
                size="small"
              >
                <ToggleButton value="today">Today</ToggleButton>
                <ToggleButton value="week">Past Week</ToggleButton>
                <ToggleButton value="month">Past Month</ToggleButton>
              </ToggleButtonGroup>
            </div>
            
            <div className="mb-4">
              <h2 className="font-bold mb-2">Activity Log</h2>
              
              {(apiDockHistory[selectedHistoryDoor.id] || []).length === 0 ? (
                <p className="text-sm text-gray-500">No historical activity for this door</p>
              ) : (
                <div className="space-y-3">
                  {(apiDockHistory[selectedHistoryDoor.id] || []).map((entry, index) => (
                    <div 
                      key={index} 
                      className={`p-3 border rounded ${
                        entry.activity === 'load' ? 'bg-orange-50 border-orange-200' :
                        entry.activity === 'unload' ? 'bg-blue-50 border-blue-200' :
                        'bg-purple-50 border-purple-200'
                      }`}
                    >
                      <div className="flex justify-between">
                        <span className="font-bold">{entry.driverName}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          entry.activity === 'load' ? 'bg-orange-100 text-orange-800' :
                          entry.activity === 'unload' ? 'bg-blue-100 text-blue-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {entry.activity.charAt(0).toUpperCase() + entry.activity.slice(1)}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">Shipment: {entry.shipmentId}</p>
                      
                      <div className="flex justify-between text-xs text-gray-500 mt-2">
                        <span>Arrival: {entry.arrivalTime}</span>
                        <span>Departure: {entry.departureTime}</span>
                        <span>Duration: {entry.duration} min</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mt-5">
              <h2 className="font-bold mb-2">Statistics for Door {selectedHistoryDoor.number}</h2>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Total Operations</p>
                  <p className="text-xl font-bold">{(apiDockHistory[selectedHistoryDoor.id] || []).length}</p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Avg. Duration</p>
                  <p className="text-xl font-bold">
                    {(apiDockHistory[selectedHistoryDoor.id] || []).length > 0
                      ? Math.round((apiDockHistory[selectedHistoryDoor.id] || []).reduce((sum, entry) => sum + entry.duration, 0) / 
                          (apiDockHistory[selectedHistoryDoor.id] || []).length)
                      : 0} min
                  </p>
                </div>
                
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Utilization</p>
                  <p className="text-xl font-bold">
                    {(apiDockHistory[selectedHistoryDoor.id] || []).length > 0
                      ? Math.round(((apiDockHistory[selectedHistoryDoor.id] || []).reduce((sum, entry) => sum + entry.duration, 0) / 
                          (timeRange === 'today' ? 24 * 60 : timeRange === 'week' ? 7 * 24 * 60 : 30 * 24 * 60)) * 100)
                      : 0}%
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-5 pt-3 border-t flex justify-end">
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
                onClick={handleCloseHistoryModal}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Yard Move Modal */}
      {showYardMoveModal && selectedDoor && (
        <Modal
          open={showYardMoveModal}
          onClose={() => {
            setShowYardMoveModal(false);
            setSelectedYardSpot('');
            setSelectedYardSpotId('');
            setSelectedShipmentreference_number('');
            setAutoCompleteMoveToYard(false);
            setMovingToYard(false);
            setMoveToYardError(null);
          }}
          className="flex justify-center items-center"
        >
          <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
            {/* Stale data overlay - only show if THIS specific door has changed */}
            {hasStagedDataForResource(selectedDoor.id) && (
              <div className="absolute inset-0 bg-gray-900 bg-opacity-60 z-50 flex items-center justify-center rounded">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full">
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                    Data Updated
                  </h3>
                  <p className="text-sm text-center text-gray-600 mb-4">
                    This information has been updated. Please close and reopen this modal to see the latest data.
                  </p>
                  <button
                    onClick={() => {
                      setShowYardMoveModal(false);
                      setSelectedYardSpot('');
                      setSelectedYardSpotId('');
                      setSelectedShipmentreference_number('');
                      setAutoCompleteMoveToYard(false);
                      setMovingToYard(false);
                      setMoveToYardError(null);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                  >
                    Close Modal
                  </button>
                </div>
              </div>
            )}
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={() => {
                setShowYardMoveModal(false);
                setSelectedYardSpot('');
                setSelectedYardSpotId('');
                setSelectedShipmentreference_number('');
                setAutoCompleteMoveToYard(false);
                setMovingToYard(false);
                setMoveToYardError(null);
              }}
            />
            <h1 className="text-xl font-bold mb-4">Move to Yard - Door {selectedDoor.number}</h1>
            
            {/* Door details */}
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <span className="font-semibold">Door:</span> {selectedDoor.number}
                </div>
                {selectedDoor.driverName && (
                  <div>
                    <span className="font-semibold">Driver:</span> {selectedDoor.driverName}
                  </div>
                )}
                {selectedDoor.trailerNumber && (
                  <div>
                    <span className="font-semibold">Trailer:</span> {selectedDoor.trailerNumber}
                  </div>
                )}
                {selectedDoor.referenceNumber && (
                  <div className="col-span-2">
                    <span className="font-semibold">Reference:</span> {selectedDoor.referenceNumber}
                  </div>
                )}
                {selectedDoor.carrier_name && (
                  <div className="col-span-2">
                    <span className="font-semibold">SCAC:</span> {selectedDoor.carrier_name}
                  </div>
                )}
              </div>
            </div>
            
            {/* Show error if any */}
            {moveToYardError && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                {moveToYardError}
              </div>
            )}
            
            {/* Shipment Reference Number field */}
            <div className="mb-4">
              <h2 className="font-semibold mb-2">Shipment Reference Number (Optional)</h2>
              <TextField
                label="Enter Reference Number (Optional)"
                variant="outlined"
                fullWidth
                value={selectedShipmentreference_number}
                onChange={(e) => setSelectedShipmentreference_number(e.target.value)}
                helperText={selectedDoor?.referenceNumber ? "Reference number is read-only when already assigned" : "Enter the shipment reference number if available (e.g. REF12345)"}
                disabled={validatingShipment || movingToYard || !!selectedDoor?.referenceNumber}
              />
            </div>
            
            {/* Auto-complete checkbox */}
            <div className="mb-4">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoCompleteMoveToYard}
                    onChange={(e) => setAutoCompleteMoveToYard(e.target.checked)}
                    disabled={movingToYard || validatingShipment}
                  />
                }
                label="Auto-complete task (task will be marked as completed immediately)"
              />
            </div>
            
            {/* Available yard spots selection */}
            <div className="mb-4">
              <h2 className="font-semibold mb-2">Select Yard Spot</h2>
              
              <div className="space-y-3">
                {/* Manual entry */}
                <TextField
                  label="Enter Yard Spot ID"
                  variant="outlined"
                  fullWidth
                  value={selectedYardSpot}
                  onChange={(e) => setSelectedYardSpot(e.target.value)}
                  helperText="Enter the yard spot identifier (e.g. A15)"
                />
                
                {/* Quick selection from available spots */}
                <div>
                  <div className="text-sm text-gray-600 mb-1">Or select from available spots:</div>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      // Get available spots - show all available spots that are not locked
                      const availableSpots = yardSpots
                        .filter((spot: YardSpot) => spot.status === 'available' && !mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id));
                        
                      if (availableSpots.length === 0) {
                        return (
                          <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                            No available yard spots found. Please enter a yard spot ID manually.
                          </div>
                        );
                      }
                      
                      return availableSpots.map((spot: YardSpot) => {
                        // Find the zone name for this spot
                        const zone = zones.find(z => z.id === spot.zoneId);
                        
                        return (
                          <button
                            key={spot.id}
                            className={`px-2 py-1 border rounded text-sm ${
                              selectedYardSpotId === spot.id 
                                ? 'bg-blue-100 border-blue-500' 
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() => {
                              setSelectedYardSpot(spot.displayNumber);
                              setSelectedYardSpotId(spot.id);
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{spot.displayNumber}</span>
                              {zone && (
                                <span className="text-xs text-gray-500">{zone.name}</span>
                              )}
                            </div>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between pt-3 border-t">
              <Button onClick={() => {
                setShowYardMoveModal(false);
                setSelectedYardSpot('');
                setSelectedYardSpotId('');
                setAutoCompleteMoveToYard(false);
                setMovingToYard(false);
              }}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={async () => {
                  if (!selectedYardSpot.trim()) return;
                  
                  setMovingToYard(true);
                  setMoveToYardError(null);
                  
                  let shipmentId = null;
                  
                  // Only validate reference number if one is provided
                  if (selectedShipmentreference_number && selectedShipmentreference_number.trim()) {
                    setValidatingShipment(true);
                    
                    try {
                      // Get auth token from cookie or localStorage
                      const authToken = getAuthToken();

                      // Validate that the shipment reference number exists
                      const shipmentValidationQuery = `
                        query GetShipmentIdByreference_number {
                          shipment_without_trailer_by_reference_number(reference_number: "${selectedShipmentreference_number.trim()}", facility_id: "${selectedFacility?.id || "1"}", trailer_uuid: "${selectedDoor?.trailerUuid || ""}") {
                            id
                            referenceNumber
                            direction
                            status
                          }
                        }
                      `;

                      const validationResponse = await fetch(GRAPHQL_API_URL, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${authToken}`
                        },
                        body: JSON.stringify({
                          query: shipmentValidationQuery
                        })
                      });

                      const validationResult = await validationResponse.json();
                      
                      if (validationResult.errors) {
                        // Check if the error is "Shipment not found"
                        const shipmentNotFoundError = validationResult.errors.find(
                          (error: any) => error.message === "Shipment not found" && 
                          error.path && error.path.includes("shipment_without_trailer_by_reference_number")
                        );
                        
                        if (shipmentNotFoundError) {
                          setMoveToYardError(`No shipment found with reference number "${selectedShipmentreference_number}". Please verify the reference number.`);
                          return;
                        }
                        
                        // For other errors, throw them
                        throw new Error(validationResult.errors[0].message);
                      }

                      // Check if shipment was found
                      const shipment = validationResult.data?.shipment_without_trailer_by_reference_number;
                      if (!shipment || !shipment.id) {
                        setMoveToYardError(`No shipment found with reference number "${selectedShipmentreference_number}". Please verify the reference number.`);
                        return;
                      }

                      console.log('Shipment validation successful:', shipment);
                      console.log('Extracted shipment ID:', shipment.id);
                      shipmentId = shipment.id;
                      setValidatingShipment(false);
                    } catch (err) {
                      console.error('Error validating shipment:', err);
                      setMoveToYardError('Failed to validate reference number. Please try again.');
                      setValidatingShipment(false);
                      setMovingToYard(false);
                      return;
                    }
                  }
                  
                  try {
                    // Get auth token
                    const authToken = getAuthToken();

                    // Find the selected yard spot details using UUID for exact match
                    const selectedSpot = yardSpots.find((spot: YardSpot) => spot.id === selectedYardSpotId);
                    if (!selectedSpot) {
                      alert('Selected yard spot not found');
                      return;
                    }

                    // Create movement task mutation for dock to yard movement
                    const createMovementTaskMutation = `
                      mutation CreateMovementTask(
                        $trailer_uuid: String!,
                        $movement_type: String!,
                        $facility_id: ID!,
                        $facility_zone_uuid: String,
                        $yard_spot_uuid: String,
                        $source_facility_zone_uuid: String,
                        $destination_facility_zone_uuid: String,
                        $shipment_id: String,
                        $force: Boolean,
                        $auto_complete: Boolean
                      ) {
                        create_movement_task(input: {
                          trailer_uuid: $trailer_uuid
                          movement_type: $movement_type
                          facility_id: $facility_id
                          facility_zone_uuid: $facility_zone_uuid
                          yard_spot_uuid: $yard_spot_uuid
                          source_facility_zone_uuid: $source_facility_zone_uuid
                          destination_facility_zone_uuid: $destination_facility_zone_uuid
                          shipment_id: $shipment_id
                          force: $force
                          auto_complete: $auto_complete
                        }) {
                          uuid
                          data
                          task_type {
                            uuid
                            type
                          }
                          task_status {
                            uuid
                            status
                          }
                          from_location
                          to_location
                          trailer {
                            uuid
                            trailer_number
                          }
                        }
                      }
                    `;

                    // Prepare variables for the mutation
                    const variables: any = {
                      trailer_uuid: selectedDoor.trailerUuid || selectedDoor.trailerNumber || selectedDoor.shipmentNumber || selectedDoor.id,
                      movement_type: 'warehouse_to_yard',
                      facility_id: selectedFacility?.id?.toString() || "1",
                      facility_zone_uuid: selectedSpot.zoneId,
                      yard_spot_uuid: selectedSpot.id,
                      source_facility_zone_uuid: null,
                      destination_facility_zone_uuid: null,
                      shipment_id: shipmentId || null,
                      force: false
                    };
                    
                    // Add auto_complete if checked
                    if (autoCompleteMoveToYard) {
                      variables.auto_complete = true;
                    }

                    console.log('Creating movement task with variables:', variables);

                    const response = await fetch(GRAPHQL_API_URL, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                      },
                      body: JSON.stringify({
                        query: createMovementTaskMutation,
                        variables
                      })
                    });

                    const result = await response.json();
                    
                    if (result.errors) {
                      console.error('GraphQL errors:', result.errors);
                      alert(`Error: ${result.errors[0].message}`);
                      return;
                    }

                    // Check if the movement task was created successfully
                    if (result.data?.create_movement_task?.uuid) {
                      console.log('Movement task created successfully:', result.data.create_movement_task);
                      
                      // Lock the door to show it's in a pending state
                      setLockedDoors(prev => new Set(prev).add(selectedDoor.id));
                      
                      // Store the task data and show success modal
                      setCreatedTask(result.data.create_movement_task);
                      setShowYardMoveModal(false);
                      setSelectedYardSpot('');
                      setSelectedYardSpotId('');
                      setAutoCompleteMoveToYard(false);
                      setShowSuccessModal(true);
                      
                      // Refresh data after a delay to show the locked state
                      setTimeout(() => {
                        setTimeInterval(prev => prev + 1);
                      }, 2000);
                    } else {
                      console.error('Failed to create movement task:', result.data);
                      alert('Failed to create movement task. Please try again.');
                    }
                  } catch (error: any) {
                    console.error('Failed to move to yard:', error);
                    setMoveToYardError(error.message || 'Failed to move trailer to yard spot. Please try again.');
                  } finally {
                    setMovingToYard(false);
                  }
                }}
                disabled={!selectedYardSpot.trim() || movingToYard}
              >
                {movingToYard ? (
                  <>
                    <CircularProgress size={16} className="mr-2" />
                    Moving...
                  </>
                ) : (
                  'Confirm Move'
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Safety Check Confirmation Dialog */}
      <Dialog
        open={showSafetyCheckConfirm}
        onClose={() => {
          if (!creatingSafetyCheck) {
            setShowSafetyCheckConfirm(false);
            setSafetyCheckError(null);
          }
        }}
      >
        <DialogTitle>Add Safety Check</DialogTitle>
        <DialogContent>
          <Typography className="mb-2">
            Are you sure you want to add a safety check to Door {selectedDoor?.number}?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This will lock the dock door and trailer until the safety check task is completed.
          </Typography>
          {safetyCheckError && (
            <Typography color="error" className="mt-2">
              {safetyCheckError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSafetyCheckConfirm(false);
              setSafetyCheckError(null);
            }}
            disabled={creatingSafetyCheck}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateSafetyCheck}
            variant="contained"
            color="success"
            disabled={creatingSafetyCheck}
          >
            {creatingSafetyCheck ? 'Creating...' : 'Confirm Safety Check'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move to Dock Modal */}
      {showMoveToDockModal && selectedDoor && (
        <Modal
          open={showMoveToDockModal}
          onClose={() => {
            setShowMoveToDockModal(false);
            setSelectedTargetDockDoor('');
            setSelectedTargetDockDoorId('');
            setDockMoveFilter('');
            setSelectedShipmentreference_number('');
            setAutoCompleteMoveToYard(false);
            setMoveToDockError(null);
            setMovingToDock(false);
          }}
          className="flex justify-center items-center"
        >
          <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
            {/* Stale data overlay - only show if THIS specific door has changed */}
            {hasStagedDataForResource(selectedDoor.id) && (
              <div className="absolute inset-0 bg-gray-900 bg-opacity-60 z-50 flex items-center justify-center rounded">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full">
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                    Data Updated
                  </h3>
                  <p className="text-sm text-center text-gray-600 mb-4">
                    This information has been updated. Please close and reopen this modal to see the latest data.
                  </p>
                  <button
                    onClick={() => {
                      setShowMoveToDockModal(false);
                      setSelectedTargetDockDoor('');
                      setSelectedTargetDockDoorId('');
                      setDockMoveFilter('');
                      setSelectedShipmentreference_number('');
                      setAutoCompleteMoveToYard(false);
                      setMoveToDockError(null);
                      setMovingToDock(false);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                  >
                    Close Modal
                  </button>
                </div>
              </div>
            )}
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={() => {
                setShowMoveToDockModal(false);
                setSelectedTargetDockDoor('');
                setSelectedTargetDockDoorId('');
                setDockMoveFilter('');
                setSelectedShipmentreference_number('');
                setAutoCompleteMoveToYard(false);
                setMoveToDockError(null);
                setMovingToDock(false);
              }}
            />
            <h1 className="text-xl font-bold mb-4">Move to Another Dock Door</h1>
            
            {/* Source door details */}
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <span className="font-semibold">From Door:</span> {selectedDoor.number}
                </div>
                {selectedDoor.driverName && (
                  <div>
                    <span className="font-semibold">Driver:</span> {selectedDoor.driverName}
                  </div>
                )}
                {selectedDoor.trailerNumber && (
                  <div>
                    <span className="font-semibold">Trailer:</span> {selectedDoor.trailerNumber}
                  </div>
                )}
                {selectedDoor.referenceNumber && (
                  <div className="col-span-2">
                    <span className="font-semibold">Reference:</span> {selectedDoor.referenceNumber}
                  </div>
                )}
                {selectedDoor.carrier_name && (
                  <div className="col-span-2">
                    <span className="font-semibold">SCAC:</span> {selectedDoor.carrier_name}
                  </div>
                )}
              </div>
            </div>
            
            {/* Show error if any */}
            {moveToDockError && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                {moveToDockError}
              </div>
            )}
            
            {/* Shipment Reference Number field */}
            <div className="mb-4">
              <h2 className="font-semibold mb-2">Shipment Reference Number (Optional)</h2>
              <TextField
                label="Enter Reference Number (Optional)"
                variant="outlined"
                fullWidth
                value={selectedShipmentreference_number}
                onChange={(e) => setSelectedShipmentreference_number(e.target.value)}
                helperText={selectedDoor?.referenceNumber ? "Reference number is read-only when already assigned" : "Enter the shipment reference number if available (e.g. REF12345)"}
                disabled={validatingShipment || movingToDock || !!selectedDoor?.referenceNumber}
              />
            </div>
            
            {/* Auto-complete checkbox */}
            <div className="mb-4">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoCompleteMoveToYard}
                    onChange={(e) => setAutoCompleteMoveToYard(e.target.checked)}
                    disabled={movingToDock || validatingShipment}
                  />
                }
                label="Auto-complete task (task will be marked as completed immediately)"
              />
            </div>
            
            {/* Target dock door selection */}
            <div className="mb-4">
              <h2 className="font-semibold mb-2">Select Target Dock Door</h2>
              
              <TextField
                label="Filter Dock Doors"
                variant="outlined"
                fullWidth
                value={dockMoveFilter}
                onChange={(e) => {
                  const filterValue = e.target.value;
                  setDockMoveFilter(filterValue);
                  
                  // Get available dock doors with new logic:
                  // 1. If door has lock → NOT Available
                  // 2. Else if no trailer → Available
                  // 3. Else if has trailer AND trailer has lock → Available
                  // 4. Else (has trailer AND no lock on trailer) → NOT Available
                  const allLocks = mergeLockSets(taskBasedLockedDoors, lockedDoors);
                  const availableDoors = apiDockDoors.filter(door => {
                    if (door.id === selectedDoor.id || !door.number || !door.number.trim()) {
                      return false;
                    }

                    if (isDockUnavailableForMovement(door)) {
                      return false;
                    }

                    // Check if door has lock
                    if (allLocks.has(door.id)) {
                      return false; // Door locked, not available
                    }

                    // Check if door has trailer
                    const hasTrailer = !!door.trailerUuid;
                    
                    if (!hasTrailer) {
                      return true; // No trailer, door available
                    }
                    
                    // Has trailer - check if trailer has lock
                    const trailerHasLock = activeLocks.some((lock: any) => {
                      const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                      return lockUuid === door.trailerUuid && 
                             (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                    });
                    
                    return trailerHasLock; // Available only if trailer is locked (being moved)
                  });
                  
                  // Auto-selection logic
                  if (filterValue.trim()) {
                    // Find dock doors that match the filter
                    const matchingDoors = availableDoors.filter(door => 
                      door.number && 
                      door.number.trim() && 
                      door.number.toLowerCase().includes(filterValue.toLowerCase())
                    );
                    
                    // Check for exact match
                    const exactMatch = matchingDoors.find(door => 
                      door.number && 
                      door.number.trim() && 
                      door.number.toLowerCase() === filterValue.toLowerCase()
                    );
                    
                    // Auto-select if there's exactly 1 result and it's an exact match
                    if (matchingDoors.length === 1 && exactMatch && exactMatch.number.trim()) {
                      setSelectedTargetDockDoor(exactMatch.number);
                      setSelectedTargetDockDoorId(exactMatch.id);
                    } else {
                      // Clear selection if multiple results or no exact match
                      setSelectedTargetDockDoor('');
                      setSelectedTargetDockDoorId('');
                    }
                  } else {
                    // Clear selection when filter is empty
                    setSelectedTargetDockDoor('');
                    setSelectedTargetDockDoorId('');
                  }
                }}
                helperText="Type to filter dock doors below (e.g. D10)"
                placeholder="Type dock door name to filter..."
              />
              
              {/* Quick selection from available dock doors */}
              <div className="mt-3">
                <div className="text-sm text-gray-600 mb-1">Or select from available doors:</div>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    // Get available dock doors with new logic:
                    // 1. If door has lock → NOT Available
                    // 2. Else if no trailer → Available (no move-from task)
                    // 3. Else if has trailer AND trailer has lock → Available (but mark as having move-from task)
                    // 4. Else (has trailer AND no lock on trailer) → NOT Available
                    const allLocks = mergeLockSets(taskBasedLockedDoors, lockedDoors);
                    const availableDoorsWithStatus = apiDockDoors
                      .filter(door => {
                        const hasValidNumber = door.number && door.number.trim();
                        const isNotCurrentDoor = door.id !== selectedDoor.id;
                        const matchesFilter = !dockMoveFilter.trim() || (hasValidNumber && door.number.toLowerCase().includes(dockMoveFilter.toLowerCase()));
                        
                        if (!hasValidNumber || !isNotCurrentDoor || !matchesFilter) {
                          return false;
                        }

                        if (isDockUnavailableForMovement(door)) {
                          return false;
                        }

                        // Check if door has lock
                        if (allLocks.has(door.id)) {
                          return false; // Door locked, not available
                        }
                        
                        // Check if door has trailer
                        const hasTrailer = !!door.trailerUuid;
                        
                        if (!hasTrailer) {
                          return true; // No trailer, door available
                        }
                        
                        // Has trailer - check if trailer has lock
                        const trailerHasLock = activeLocks.some((lock: any) => {
                          const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                          return lockUuid === door.trailerUuid && 
                                 (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                        });
                        
                        return trailerHasLock; // Available only if trailer is locked (being moved)
                      })
                      .map(door => {
                        // Check if this door has a move-from task (trailer with lock)
                        const hasTrailer = !!door.trailerUuid;
                        const hasMoveFromTask = hasTrailer && activeLocks.some((lock: any) => {
                          const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                          return lockUuid === door.trailerUuid && 
                                 (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                        });
                        
                        return { ...door, hasMoveFromTask };
                      });
                    const availableDoors = availableDoorsWithStatus;
                      
                    if (availableDoors.length === 0) {
                      return (
                        <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                          {dockMoveFilter.trim() 
                            ? `No dock doors found matching "${dockMoveFilter}". Try clearing the filter.`
                            : `No available dock doors found.`
                          }
                        </div>
                      );
                    }
                    
                    return availableDoors.map(door => (
                      <button
                        key={door.id}
                        className={`px-2 py-1 border rounded text-sm ${
                          selectedTargetDockDoorId === door.id 
                            ? 'bg-blue-100 border-blue-500' 
                            : door.hasMoveFromTask 
                              ? 'bg-gray-300 hover:bg-gray-400' 
                              : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          setSelectedTargetDockDoor(door.number);
                          setSelectedTargetDockDoorId(door.id);
                          setDockMoveFilter(door.number);
                        }}
                        title={door.hasMoveFromTask ? 'Door has an active move-from task' : undefined}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{door.number}</span>
                          {door.zoneName && (
                            <span className="text-xs text-gray-500">{door.zoneName}</span>
                          )}
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              </div>
            </div>
            
            <div className="flex justify-between pt-3 border-t">
              <Button onClick={() => {
                setShowMoveToDockModal(false);
                setSelectedTargetDockDoor('');
                setSelectedTargetDockDoorId('');
                setDockMoveFilter('');
                setSelectedShipmentreference_number('');
                setAutoCompleteMoveToYard(false);
                setMoveToDockError(null);
                setMovingToDock(false);
              }}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleMoveTrailerToDock}
                disabled={!selectedTargetDockDoor.trim() || movingToDock || validatingShipment}
              >
                {validatingShipment ? (
                  <>
                    <CircularProgress size={16} className="mr-2" />
                    Validating...
                  </>
                ) : movingToDock ? (
                  <>
                    <CircularProgress size={16} className="mr-2" />
                    Moving...
                  </>
                ) : (
                  'Move to Dock'
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Success Modal */}
      {showSuccessModal && createdTask && (
        <Modal
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            setCreatedTask(null);
          }}
          className="flex justify-center items-center"
        >
          <div className="w-[500px] bg-white border p-6 max-h-[90vh] overflow-y-auto relative">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={() => {
                setShowSuccessModal(false);
                setCreatedTask(null);
              }}
            />
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-green-600 text-2xl" />
              </div>
              <h1 className="text-xl font-bold text-green-700 mb-2">Movement Task Created Successfully!</h1>
              <p className="text-gray-600">The trailer has been scheduled to move to the yard spot.</p>
            </div>
            
            {/* Task Details */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
              <h2 className="font-semibold mb-3 text-gray-800">Task Details</h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Task ID:</span>
                  <span className="text-gray-800 font-mono text-sm">{createdTask.uuid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Task Type:</span>
                  <span className="text-gray-800">{createdTask.task_type?.type || 'Movement Task'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Status:</span>
                  <span className="text-gray-800">{createdTask.task_status?.status || 'Created'}</span>
                </div>
                {createdTask.from_location && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">From:</span>
                    <span className="text-gray-800">{createdTask.from_location}</span>
                  </div>
                )}
                {createdTask.to_location && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">To:</span>
                    <span className="text-gray-800">{createdTask.to_location}</span>
                  </div>
                )}
                {createdTask.trailer?.trailer_number && (
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Trailer:</span>
                    <span className="text-gray-800">{createdTask.trailer.trailer_number}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t">
              <Button 
                onClick={() => {
                  setShowSuccessModal(false);
                  setCreatedTask(null);
                }}
              >
                Close
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => {
                  // Navigate to the task page
                  window.open(`/tasks/${createdTask.uuid}`, '_blank');
                }}
                startIcon={<ArrowForward />}
              >
                View Task Details
              </Button>
            </div>
          </div>
        </Modal>
      )}


      {/* Configuration Modal */}
      <Modal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        className="flex items-center justify-center"
      >
        <div className="bg-white rounded-lg p-6 w-[800px] max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Dock Configuration</h2>
            <button onClick={() => setShowConfigModal(false)}>
              <Close />
            </button>
          </div>
          
          <Tabs value={configModalTab} onChange={handleConfigModalTabChange}>
            <Tab label="Indicator Colors" />
            <Tab label="Card Colors" />
            <Tab label="Thresholds" />
          </Tabs>
          
          <div className="mt-4">
            {configModalTab === 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold">MDT Timer Colors</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Warning Color</label>
                    <input
                      type="color"
                      value={colorConfig.mdt.warning}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        mdt: { ...prev.mdt, warning: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Critical Color</label>
                    <input
                      type="color"
                      value={colorConfig.mdt.critical}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        mdt: { ...prev.mdt, critical: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Good Color</label>
                    <input
                      type="color"
                      value={colorConfig.mdt.good}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        mdt: { ...prev.mdt, good: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Text Color</label>
                    <input
                      type="color"
                      value={colorConfig.mdt.textColor}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        mdt: { ...prev.mdt, textColor: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                </div>

                <h3 className="font-semibold mt-6">Detention Timer Colors</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Warning Color</label>
                    <input
                      type="color"
                      value={colorConfig.detention.warning}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        detention: { ...prev.detention, warning: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Critical Color</label>
                    <input
                      type="color"
                      value={colorConfig.detention.critical}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        detention: { ...prev.detention, critical: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Good Color</label>
                    <input
                      type="color"
                      value={colorConfig.detention.good}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        detention: { ...prev.detention, good: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Text Color</label>
                    <input
                      type="color"
                      value={colorConfig.detention.textColor}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        detention: { ...prev.detention, textColor: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                </div>

                <h3 className="font-semibold mt-6">Dock Timer Colors</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Warning Color</label>
                    <input
                      type="color"
                      value={colorConfig.dockTimer.warning}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        dockTimer: { ...prev.dockTimer, warning: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Critical Color</label>
                    <input
                      type="color"
                      value={colorConfig.dockTimer.critical}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        dockTimer: { ...prev.dockTimer, critical: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Good Color</label>
                    <input
                      type="color"
                      value={colorConfig.dockTimer.good}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        dockTimer: { ...prev.dockTimer, good: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Text Color</label>
                    <input
                      type="color"
                      value={colorConfig.dockTimer.textColor}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        dockTimer: { ...prev.dockTimer, textColor: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {configModalTab === 1 && (
              <div className="space-y-4">
                <h3 className="font-semibold">Card Colors</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Default Background</label>
                    <input
                      type="color"
                      value={colorConfig.card.default}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, default: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Text Color</label>
                    <input
                      type="color"
                      value={colorConfig.card.textColor}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, textColor: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">One Warning</label>
                    <input
                      type="color"
                      value={colorConfig.card.oneWarning}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, oneWarning: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Two Warnings</label>
                    <input
                      type="color"
                      value={colorConfig.card.twoWarnings}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, twoWarnings: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Three Warnings</label>
                    <input
                      type="color"
                      value={colorConfig.card.threeWarnings}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, threeWarnings: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">One Critical</label>
                    <input
                      type="color"
                      value={colorConfig.card.oneCritical}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, oneCritical: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Two Criticals</label>
                    <input
                      type="color"
                      value={colorConfig.card.twoCriticals}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, twoCriticals: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Three Criticals</label>
                    <input
                      type="color"
                      value={colorConfig.card.threeCriticals}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, threeCriticals: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mixed State</label>
                    <input
                      type="color"
                      value={colorConfig.card.mixed}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        card: { ...prev.card, mixed: e.target.value }
                      }))}
                      className="mt-1 block w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {configModalTab === 2 && (
              <div className="space-y-4">
                <h3 className="font-semibold">MDT Timer Thresholds</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Warning Threshold (minutes)</label>
                    <input
                      type="number"
                      value={colorConfig.mdt.warningThreshold}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        mdt: { ...prev.mdt, warningThreshold: parseInt(e.target.value) }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Critical Threshold (minutes)</label>
                    <input
                      type="number"
                      value={colorConfig.mdt.criticalThreshold}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        mdt: { ...prev.mdt, criticalThreshold: parseInt(e.target.value) }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <h3 className="font-semibold mt-6">Detention Timer Thresholds</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Warning Threshold (minutes)</label>
                    <input
                      type="number"
                      value={colorConfig.detention.warningThreshold}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        detention: { ...prev.detention, warningThreshold: parseInt(e.target.value) }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Critical Threshold (minutes)</label>
                    <input
                      type="number"
                      value={colorConfig.detention.criticalThreshold}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        detention: { ...prev.detention, criticalThreshold: parseInt(e.target.value) }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <h3 className="font-semibold mt-6">Dock Timer Thresholds</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Warning Threshold (minutes)</label>
                    <input
                      type="number"
                      value={colorConfig.dockTimer.warningThreshold}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        dockTimer: { ...prev.dockTimer, warningThreshold: parseInt(e.target.value) }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Critical Threshold (minutes)</label>
                    <input
                      type="number"
                      value={colorConfig.dockTimer.criticalThreshold}
                      onChange={(e) => setColorConfig(prev => ({
                        ...prev,
                        dockTimer: { ...prev.dockTimer, criticalThreshold: parseInt(e.target.value) }
                      }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() => setShowConfigModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={saveConfiguration}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              Save Configuration
            </button>
          </div>
        </div>
      </Modal>

      {/* Unified Move to Yard/Zone Modal - Exact same as YardSpotManagement */}
      {showMoveToYardModal && selectedDoor && (
        <Modal
          open={showMoveToYardModal}
          onClose={handleCloseMoveToYardModal}
        >
          <div className="flex justify-center items-center w-full h-full">
            <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
              {/* Stale data overlay - only show if THIS specific door has changed */}
              {hasStagedDataForResource(selectedDoor.id) && (
                <div className="absolute inset-0 bg-gray-900 bg-opacity-60 z-50 flex items-center justify-center rounded">
                  <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
                    <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                      Data Updated
                    </h3>
                    <p className="text-sm text-center text-gray-600 mb-4">
                      This information has been updated. Please close and reopen this modal to see the latest data.
                    </p>
                    <button
                      onClick={handleCloseMoveToYardModal}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
                    >
                      Close Modal
                    </button>
                  </div>
                </div>
              )}
              <Close
                className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                onClick={handleCloseMoveToYardModal}
              />
              <h1 className="text-xl font-bold mb-4">Move from Dock to Yard</h1>
              
              {/* Door details */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <span className="font-semibold">From Door:</span> {selectedDoor.number}
                  </div>
                  {selectedDoor.driverName && (
                    <div>
                      <span className="font-semibold">Driver:</span> {selectedDoor.driverName}
                    </div>
                  )}
                  {selectedDoor.trailerNumber && (
                    <div>
                      <span className="font-semibold">Trailer:</span> {selectedDoor.trailerNumber}
                    </div>
                  )}
                  {selectedDoor.referenceNumber && (
                    <div className="col-span-2">
                      <span className="font-semibold">Shipment:</span> {selectedDoor.referenceNumber}
                    </div>
                  )}
                  {selectedDoor.carrier_name && (
                    <div className="col-span-2">
                      <span className="font-semibold">SCAC:</span> {selectedDoor.carrier_name}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Show error if any */}
              {moveToDestinationError && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {moveToDestinationError}
                </div>
              )}
              
              {/* Shipment Reference Number field - only show if prefilled */}
              {selectedShipmentreference_number && (
                <div className="mb-4">
                  <h2 className="font-semibold mb-2">Shipment Reference Number</h2>
                  <TextField
                    label="Shipment Reference Number"
                    variant="outlined"
                    fullWidth
                    value={selectedShipmentreference_number}
                    onChange={(e) => setSelectedShipmentreference_number(e.target.value)}
                    helperText={selectedDoor?.referenceNumber ? "Reference number is read-only when already assigned" : "Enter the shipment reference number if available"}
                    disabled={validatingShipmentForYard || movingToDestination || !!selectedDoor?.referenceNumber}
                  />
                </div>
              )}
              
              {/* Auto-complete checkbox */}
              <div className="mb-4">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoCompleteMoveToYard}
                      onChange={(e) => setAutoCompleteMoveToYard(e.target.checked)}
                      disabled={movingToDestination || validatingShipmentForYard}
                    />
                  }
                  label="Auto-complete task (task will be marked as completed immediately)"
                />
              </div>
              
              {/* Destination Type Selection */}
              <div className="mb-4">
                <h2 className="font-semibold mb-2">Select Destination Type</h2>
                <ToggleButtonGroup
                  value={selectedDestinationType}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      setSelectedDestinationType(value);
                      setSelectedYardSpotForMove('');
                      setSelectedFacilityZone('');
                      setMoveToDestinationError(null);
                    }
                  }}
                  fullWidth
                >
                  <ToggleButton value="yard_spot">
                    Yard Spot
                  </ToggleButton>
                  <ToggleButton value="facility_zone">
                    Facility Zone
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              {selectedDestinationType === 'yard_spot' && (
                <div>
                  {/* Zone filter dropdown */}
                  <div className="mb-4">
                    <h2 className="font-semibold mb-2">Filter by Zone (Optional)</h2>
                    <Autocomplete
                      options={zones.map(zone => ({ 
                        label: zone.name, 
                        value: zone.id,
                        color: zone.color 
                      }))}
                      value={selectedZoneFilter ? zones.find(z => z.id === selectedZoneFilter) ? { 
                        label: zones.find(z => z.id === selectedZoneFilter)!.name, 
                        value: selectedZoneFilter,
                        color: zones.find(z => z.id === selectedZoneFilter)!.color
                      } : null : null}
                      onChange={(_, newValue) => {
                        setSelectedZoneFilter(newValue?.value || null);
                        // Clear selected yard spot and spot filter when zone filter changes
                        setSelectedYardSpotForMove('');
                        setSelectedYardSpotUuid('');
                        setSpotFilter('');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Zone"
                          variant="outlined"
                          fullWidth
                          helperText="Filter yard spots by zone"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded mr-2" 
                              style={{ backgroundColor: option.color }}
                            />
                            {option.label}
                          </div>
                        </li>
                      )}
                    />
                  </div>

                  {/* Available yard spots selection */}
                  <div className="mb-4">
                    <h2 className="font-semibold mb-2">Select Yard Spot</h2>
                    
                    <div className="space-y-3">
                      {/* Spot filter field */}
                      <TextField
                        label="Filter Yard Spots"
                        variant="outlined"
                        fullWidth
                        value={spotFilter}
                        onChange={(e) => {
                          const filterValue = e.target.value;
                          setSpotFilter(filterValue);
                          
                          // Get available spots filtered by zone and spot filter
                          const availableSpots = yardSpots.filter(spot => {
                            const isAvailable = spot.status === 'available' && !mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id);
                            const matchesZone = !selectedZoneFilter || spot.zoneId === selectedZoneFilter;
                            return isAvailable && matchesZone;
                          });
                          
                          // Auto-selection logic
                          if (filterValue.trim()) {
                            // Find spots that match the filter (only spots with valid displayNumber)
                            const matchingSpots = availableSpots.filter(spot => 
                              spot.displayNumber && 
                              spot.displayNumber.trim() && 
                              spot.displayNumber.toLowerCase().includes(filterValue.toLowerCase())
                            );
                            
                            // Check for exact match (only spots with valid displayNumber)
                            const exactMatch = matchingSpots.find(spot => 
                              spot.displayNumber && 
                              spot.displayNumber.trim() && 
                              spot.displayNumber.toLowerCase() === filterValue.toLowerCase()
                            );
                            
                            // Auto-select if there's exactly 1 result and it's an exact match
                            if (matchingSpots.length === 1 && exactMatch && exactMatch.displayNumber.trim()) {
                              setSelectedYardSpotForMove(exactMatch.displayNumber);
                              setSelectedYardSpotUuid(exactMatch.id);
                            } else {
                              // Clear selection if multiple results or no exact match
                              setSelectedYardSpotForMove('');
                              setSelectedYardSpotUuid('');
                            }
                          } else {
                            // Clear selection when filter is empty
                            setSelectedYardSpotForMove('');
                            setSelectedYardSpotUuid('');
                          }
                        }}
                        helperText="Type to filter spots below (e.g. A15)"
                        placeholder="Type spot name to filter..."
                      />
                    
                    {/* Quick selection from available spots */}
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Or select from available spots:</div>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          // Get available spots - filtered by zone and spot filter (only spots with valid displayNumber)
                          const availableSpots = yardSpots.filter(spot => {
                            const hasValidDisplayNumber = spot.displayNumber && spot.displayNumber.trim();
                            const isAvailable = spot.status === 'available' && !mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id);
                            const matchesZone = !selectedZoneFilter || spot.zoneId === selectedZoneFilter;
                            const matchesSpotFilter = !spotFilter.trim() || (hasValidDisplayNumber && spot.displayNumber.toLowerCase().includes(spotFilter.toLowerCase()));
                            return hasValidDisplayNumber && isAvailable && matchesZone && matchesSpotFilter;
                          });
                            
                          if (availableSpots.length === 0) {
                            return (
                              <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                                {spotFilter.trim() && selectedZoneFilter 
                                  ? `No available yard spots found matching "${spotFilter}" in the selected zone. Try clearing the filters.`
                                  : spotFilter.trim()
                                  ? `No available yard spots found matching "${spotFilter}". Try clearing the filter.`
                                  : selectedZoneFilter 
                                  ? `No available yard spots found in the selected zone. Try clearing the zone filter.`
                                  : `No available yard spots found.`
                                }
                              </div>
                            );
                          }
                          
                          return availableSpots.map(spot => {
                            // Find the zone name for this spot
                            const zone = zones.find(z => z.id === spot.zoneId);
                            
                            return (
                              <button
                                key={spot.id}
                                className={`px-2 py-1 border rounded text-sm ${
                                  selectedYardSpotUuid === spot.id 
                                    ? 'bg-blue-100 border-blue-500' 
                                    : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                                onClick={() => {
                                  setSelectedYardSpotForMove(spot.displayNumber);
                                  setSelectedYardSpotUuid(spot.id);
                                  setSpotFilter(spot.displayNumber);
                                }}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{spot.displayNumber}</span>
                                  {zone && (
                                    <span className="text-xs text-gray-500">{zone.name}</span>
                                  )}
                                </div>
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              )}

              {selectedDestinationType === 'facility_zone' && (
                /* Facility zone selection */
                <div className="mb-4">
                  <h2 className="font-semibold mb-2">Select Facility Zone</h2>
                  
                  <div className="space-y-3">
                    {/* Quick selection from available facility zones */}
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Select from available facility zones:</div>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          // Get available facility zones
                          const availableFacilityZones = facilityZones || [];
                          
                          if (availableFacilityZones.length === 0) {
                            return (
                              <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                                No facility zones found.
                              </div>
                            );
                          }
                          
                          return availableFacilityZones.map((zone: any) => {
                            return (
                              <button
                                key={zone.facility_zone_uuid}
                                className={`px-3 py-2 border rounded text-sm ${
                                  selectedFacilityZone === zone.facility_zone_uuid 
                                    ? 'bg-blue-100 border-blue-500' 
                                    : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                                onClick={() => setSelectedFacilityZone(zone.facility_zone_uuid)}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{zone.facility_zone_name}</span>
                                  {zone.zone_type && (
                                    <span className="text-xs text-gray-500">{zone.zone_type}</span>
                                  )}
                                </div>
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between pt-3 border-t">
                <Button onClick={handleCloseMoveToYardModal}>Cancel</Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleMoveToDestination}
                  disabled={
                    (selectedDestinationType === 'yard_spot' && !selectedYardSpotUuid.trim()) ||
                    (selectedDestinationType === 'facility_zone' && !selectedFacilityZone.trim()) ||
                    movingToDestination || validatingShipmentForYard
                  }
                >
                  {validatingShipmentForYard ? (
                    <>
                      <CircularProgress size={24} className="mr-2" />
                      Validating...
                    </>
                  ) : movingToDestination ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Move from Dock'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Task Details Modal */}
      <TaskDetailsModal
        open={showTaskDetailsModal}
        onClose={() => {
          setShowTaskDetailsModal(false);
          setSelectedTaskForDetails(null);
        }}
        task={selectedTaskForDetails}
        onCancelTask={handleCancelTask}
        onCompleteTask={handleCompleteTask}
        onCompleteMovementTask={handleCompleteMovementTask}
      />

      {/* Remove Shipment Confirmation Dialog */}
      <Dialog
        open={showRemoveShipmentConfirm}
        onClose={() => setShowRemoveShipmentConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete />
            Remove Shipment Relationship
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove the shipment relationship from trailer <strong>{selectedDoor?.trailerNumber}</strong> at door <strong>{selectedDoor?.number}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current shipment: <strong>{selectedDoor?.referenceNumber || selectedDoor?.shipmentNumber}</strong>
          </Typography>
          <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'medium' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowRemoveShipmentConfirm(false)}
            disabled={isRemovingShipment}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveShipmentRelationship}
            variant="contained"
            color="error"
            disabled={isRemovingShipment}
            startIcon={isRemovingShipment ? <CircularProgress size={16} /> : <Delete />}
          >
            {isRemovingShipment ? 'Removing...' : 'Remove Relationship'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DockManagement; 